var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { defineComponent, getCurrentInstance, provide, computed, unref, inject, onActivated, onMounted, queuePostFlushCb, onBeforeUnmount, onBeforeMount, onUnmounted, onUpdated, warn, h, setTransitionHooks, shallowRef, callWithAsyncErrorHandling, ErrorCodes, resolveComponent, openBlock, createElementBlock, createElementVNode, createVNode, mergeProps, Transition, withCtx, createBlock, createCommentVNode, ref, onScopeDispose, customRef, isRef, reactive, markRaw, proxyRefs, TransitionGroup, Fragment, renderList, onDeactivated, normalizeStyle, normalizeClass } from "vue";
/**
* @vue/shared v3.5.44
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
!!(process.env.NODE_ENV !== "production") ? Object.freeze({}) : {};
!!(process.env.NODE_ENV !== "production") ? Object.freeze([]) : [];
const invokeArrayFns = (fns, ...arg) => {
  for (let i = 0; i < fns.length; i++) {
    fns[i](...arg);
  }
};
const ShapeFlags = {
  "SUSPENSE": 128,
  "COMPONENT_SHOULD_KEEP_ALIVE": 256,
  "COMPONENT_KEPT_ALIVE": 512
};
function invokeVNodeHook(hook, instance, vnode, prevVNode = null) {
  callWithAsyncErrorHandling(hook, instance, ErrorCodes.VNODE_HOOK, [
    vnode,
    prevVNode
  ]);
}
function getInnerChild(vnode) {
  return vnode.shapeFlag & ShapeFlags.SUSPENSE ? vnode.ssContent : vnode;
}
function resetShapeFlag(vnode) {
  vnode.shapeFlag &= -257;
  vnode.shapeFlag &= -513;
}
function makeProvidesParentReactive(instance) {
  var _a;
  const reactiveInstance = shallowRef(instance);
  const originalProvides = instance.provides;
  const ownProvides = instance.provides === ((_a = instance.parent) == null ? void 0 : _a.provides) ? [] : Object.getOwnPropertyNames(originalProvides);
  const proxyProvider = new Proxy({}, {
    get(_target, key) {
      if (typeof key !== "string" || !key.startsWith("reactive_")) {
        return originalProvides[key];
      }
      if (key in ownProvides) {
        return originalProvides[key];
      }
      return computed(() => {
        var _a2;
        return unref((_a2 = reactiveInstance.value.parent) == null ? void 0 : _a2.provides[key]);
      });
    },
    // Vue valides keys using 'a' in obj, so we need to handle this correctly
    has() {
      return true;
    }
  });
  instance.provides = Object.create(proxyProvider);
  return reactiveInstance;
}
const ComponentWithPropertiesInstance = defineComponent({
  name: "ComponentWithPropertiesInstance",
  props: {
    component: {
      // The navigation module graph is circular; Vue reads this after initialization.
      get type() {
        return ComponentWithProperties;
      },
      required: true
    },
    customProvide: {
      required: false,
      type: Object,
      default: null
    }
  },
  __isKeepAlive: true,
  setup(props, context) {
    const instance = getCurrentInstance();
    const sharedContext = instance.ctx;
    const reactiveInstance = makeProvidesParentReactive(instance);
    if (props.customProvide) {
      for (const key in props.customProvide) {
        if (key.startsWith("reactive_")) {
          provide(key, computed(() => {
            var _a;
            return unref((_a = props.customProvide) == null ? void 0 : _a[key]);
          }));
        } else {
          provide(key, props.customProvide[key]);
        }
      }
    }
    provide("navigation_currentComponent", props.component);
    const parentHistory = inject("navigation_historyIndex", null);
    if (parentHistory !== null) {
      props.component.inheritHistoryIndex(parentHistory);
    }
    const parent = inject("navigation_parent", null);
    if (parent !== null) {
      props.component.inheritFromParent(parent);
    }
    provide("navigation_parentComponent", parent);
    provide("navigation_historyIndex", props.component.historyIndex);
    provide("navigation_parent", props.component);
    const combinedProvide = props.component.combinedProvide;
    for (const key in combinedProvide) {
      provide(key, combinedProvide[key]);
    }
    const disableUrl = inject("reactive_navigation_disable_url", null);
    const inheritedUrlRaw = inject("reactive_navigation_url", null);
    const updateUrl = () => {
      const url = unref(props.component.combinedProvide.reactive_navigation_url) ?? unref(inheritedUrlRaw) ?? null;
      const disableUrlUnwrapped = unref(disableUrl) ?? false;
      if (!disableUrlUnwrapped) {
        if (url && !(url instanceof ReactiveUrl)) {
          console.error("Unexpected reactive_navigation_url", url);
        } else {
          props.component.setUrl(url);
        }
      }
    };
    onActivated(() => {
      updateUrl();
    });
    onMounted(() => {
      updateUrl();
    });
    const {
      renderer: {
        p: patch,
        m: move,
        um: _unmount
      }
    } = sharedContext;
    const parentSuspense = instance.suspense;
    sharedContext.activate = (vnode, container, anchor, namespace, optimized) => {
      const instance2 = vnode.component;
      move(vnode, container, anchor, 0, parentSuspense);
      patch(
        instance2.vnode,
        vnode,
        container,
        anchor,
        instance2,
        parentSuspense,
        namespace,
        vnode.slotScopeIds,
        optimized
      );
      queuePostFlushCb(() => {
        instance2.isDeactivated = false;
        if (instance2.a) {
          invokeArrayFns(instance2.a);
        }
        const vnodeHook = vnode.props && vnode.props.onVnodeMounted;
        if (vnodeHook) {
          invokeVNodeHook(vnodeHook, instance2.parent, vnode);
        }
      });
    };
    sharedContext.deactivate = (vnode) => {
      const instance2 = vnode.component;
      queuePostFlushCb(() => {
        if (instance2.da) {
          invokeArrayFns(instance2.da);
        }
        const vnodeHook = vnode.props && vnode.props.onVnodeUnmounted;
        if (vnodeHook) {
          invokeVNodeHook(vnodeHook, instance2.parent, vnode);
        }
        instance2.isDeactivated = true;
      });
    };
    function unmount(vnode) {
      if (current) {
        const { subTree } = instance;
        const _innerVnode = getInnerChild(subTree);
        if (vnode.type === _innerVnode.type && vnode.key === _innerVnode.key) {
          resetShapeFlag(_innerVnode);
          const da = _innerVnode.component.da;
          da && queuePostFlushCb(da);
          return;
        }
      }
      resetShapeFlag(vnode);
      _unmount(vnode, instance, parentSuspense, true);
    }
    let current = null;
    function getChildVNode() {
      var _a, _b, _c, _d;
      return (_d = (_c = (_b = (_a = instance.vnode) == null ? void 0 : _a.component) == null ? void 0 : _b.subTree) == null ? void 0 : _c.component) == null ? void 0 : _d.vnode;
    }
    onBeforeUnmount(() => {
      if (!current) {
        return;
      }
      const child = getChildVNode();
      if (!child) {
        console.error("No child found in ComponentWithPropertiesInstance.beforeUnmount");
        return;
      }
      if (props.component.unmount === unmount) {
        props.component.destroy(getChildVNode());
      } else {
        console.warn("ComponentWithPropertiesInstance unmount called with different unmount function");
      }
      current = null;
    });
    onBeforeMount(() => {
      props.component.beforeMount();
    });
    onUnmounted(() => {
      props.component.onUnmounted();
    });
    let pendingCacheKey = null;
    const cacheSubtree = () => {
      if (current && pendingCacheKey != null) {
        const child = getChildVNode();
        if (!child) {
          console.error("No child found in ComponentWithPropertiesInstance.cacheSubtree");
          return;
        }
        props.component.vnode = child;
        props.component.unmount = unmount;
        if (!child._reactiveInstance) {
          child._reactiveInstance = reactiveInstance;
        }
      }
    };
    onMounted(cacheSubtree);
    onUpdated(cacheSubtree);
    return () => {
      pendingCacheKey = null;
      if (!props.component) {
        warn("No component provided to ComponentWithPropertiesInstance");
        current = null;
        return null;
      }
      props.component.component.inheritAttrs = false;
      const vnode = h(props.component.component, { ...props.component.properties, ...context.attrs });
      const comp = vnode.type;
      const key = vnode.key == null ? comp : vnode.key;
      pendingCacheKey = key;
      if (props.component.vnode) {
        const cachedVNode = props.component.vnode;
        const parent2 = instance;
        vnode.el = cachedVNode.el;
        vnode.component = cachedVNode.component;
        if (!cachedVNode._reactiveInstance) {
          console.warn("Missing _reactiveInstance on vnode");
        } else {
          cachedVNode._reactiveInstance.value = parent2;
        }
        if (vnode.transition) {
          setTransitionHooks(vnode, vnode.transition);
        }
        vnode.shapeFlag |= ShapeFlags.COMPONENT_KEPT_ALIVE;
      }
      vnode.shapeFlag |= ShapeFlags.COMPONENT_SHOULD_KEEP_ALIVE;
      current = vnode;
      return vnode;
    };
  }
});
const _sfc_main$4 = {
  name: "FramedComponent",
  components: {
    ComponentWithPropertiesInstance
  },
  provide() {
    return {
      ...this.customProvide ?? {}
    };
  },
  props: {
    root: {
      // Defer the constructor lookup until the circular navigation graph has initialized.
      get type() {
        return ComponentWithProperties;
      },
      required: true
    },
    customProvide: {
      type: Object,
      default: null
    }
  },
  emits: ["show", "push", "pop"]
};
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_ComponentWithPropertiesInstance = resolveComponent("ComponentWithPropertiesInstance");
  return openBlock(), createElementBlock("div", null, [
    createElementVNode("div", null, [
      createVNode(_component_ComponentWithPropertiesInstance, mergeProps({ component: $props.root }, _ctx.$attrs), null, 16, ["component"])
    ])
  ]);
}
const FramedComponent = /* @__PURE__ */ _export_sfc(_sfc_main$4, [["render", _sfc_render$2]]);
const NavigationController$1 = defineComponent({
  name: "NavigationController",
  components: {
    FramedComponent
  },
  inject: {
    reactive_navigation_pop: {
      default: null
    },
    reactive_navigation_can_pop: {
      default: false
    },
    reactive_navigation_can_dismiss: {
      default: false
    },
    reactive_navigationController: {
      default: null
    },
    // Our own wrapper component, used to forward our content state to a parent controller.
    navigation_currentComponent: {
      default: null
    }
  },
  provide() {
    let extra = {};
    if (this.animationType === "modal") {
      extra = {
        reactive_navigation_dismiss: computed(() => this.components.length > 1 ? this.pop : unref(this.reactive_navigation_pop)),
        reactive_navigation_can_dismiss: computed(() => this.components.length > 1),
        reactive_navigation_can_pop: false
      };
    } else {
      extra = {
        reactive_navigation_can_pop: computed(() => this.components.length > 1 || unref(this.reactive_navigation_can_pop))
      };
    }
    return {
      reactive_navigationController: this,
      reactive_navigation_show: this.push,
      reactive_navigation_pop: computed(() => this.components.length > 1 ? this.pop : unref(this.reactive_navigation_pop)),
      ...extra,
      ...this.customProvide ?? {}
    };
  },
  props: {
    /** Initial view when initialComponents is absent or empty; later prop changes do not reset the stack. */
    root: {
      default: null,
      type: Object
    },
    /** Initial bottom-to-top stack; a nonempty array takes precedence over root and only its last view is visible. */
    initialComponents: {
      default: null,
      type: Object
    },
    animationType: {
      default: "default",
      type: String
    },
    disableAnimations: {
      default: false,
      type: Boolean
    },
    customProvide: {
      type: Object,
      default: null
    }
  },
  emits: ["didPush", "didPop", "showDetail", "present"],
  data() {
    const savedInternalScrollPositions = [];
    const components = [];
    return {
      components,
      mainComponent: null,
      transitionName: "none",
      nextInternalScrollPosition: 0,
      savedInternalScrollPositions,
      // Because push/pop is async, it can cause issues if we call it multiple times after each other
      // all push/pop operations should be queued
      asyncQueue: [],
      asyncQueueRunning: false,
      cachedProvides: /* @__PURE__ */ new Map(),
      // Key of the current main component when it signaled that it does not have any
      // content yet (e.g. an async component that is still loading). This uses an opt-out
      // model: every component is assumed to have content unless it explicitly signals
      // otherwise. While set, we keep the size frozen instead of collapsing to the height
      // of the (empty) loading placeholder.
      contentlessMainKey: null,
      // Whether a content-height animation (triggered when async content arrives) is
      // currently running, so afterEnter does not clear the frozen size mid-animation.
      animatingContent: false,
      // Ownership token for content-height animations. Each new sizing operation (freezeSize)
      // or content animation increments it; pending animation-frame/transition callbacks
      // bail out when their captured token is no longer current, so an older animation can
      // never overwrite dimensions, clear animatingContent, or unfreeze a newer one.
      contentAnimationToken: 0,
      // Removes transition listeners and animation-frame callbacks for the current
      // content-height animation.
      contentTransitionCleanup: null
    };
  },
  computed: {
    navigationController() {
      return unref(this.reactive_navigationController);
    }
  },
  watch: {
    // whenever question changes, this function will run
    mainComponent(newComponent, oldComponent) {
      if (newComponent === this.root || oldComponent === null) {
        return;
      }
      this.resetContentState();
      if (!newComponent) {
        return;
      }
      this.cacheComponentProvides(newComponent);
    }
  },
  beforeMount() {
    if (this.initialComponents && this.initialComponents.length > 0) {
      this.mainComponent = this.initialComponents[this.initialComponents.length - 1];
      this.components = this.initialComponents.slice(0);
    } else {
      if (!this.root) {
        throw new Error("No root component provided for navigation controller");
      }
      this.mainComponent = this.root;
      this.components = [this.root];
    }
    for (const [index, component] of this.components.entries()) {
      this.cacheComponentProvides(component);
      if (index < this.components.length - 1) {
        HistoryManager.pushState(null, null, { adjustHistory: false });
      }
      component.assignHistoryIndex();
    }
  },
  mounted() {
    var _a;
    if (this.contentlessMainKey === ((_a = this.mainComponent) == null ? void 0 : _a.key)) {
      this.setContentState(this.mainComponent, false);
    }
  },
  beforeUnmount() {
    this.cancelContentAnimation();
    for (const component of this.components) {
      if (component.isKeptAlive && component.vnode) {
        component.destroy(component.vnode);
      }
    }
    this.components = [];
    this.mainComponent = null;
  },
  methods: {
    cacheComponentProvides(newComponent) {
      if (this.animationType === "modal") {
        this.cachedProvides.set(newComponent.key, {
          reactive_navigation_can_dismiss: this.components.length > 1
        });
      } else {
        this.cachedProvides.set(newComponent.key, {
          reactive_navigation_can_pop: this.components.length > 1 || unref(this.reactive_navigation_can_pop)
        });
      }
    },
    provideForComponent(key) {
      return this.cachedProvides.get(key) ?? {};
    },
    runQueue(run) {
      return new Promise((resolve, reject) => {
        this.asyncQueue.push(async () => {
          try {
            const r = await run();
            resolve(r);
          } catch (e) {
            reject(e);
          }
        });
        this.runQueueIfNeeded();
      });
    },
    runQueueIfNeeded() {
      if (this.asyncQueueRunning) {
        return;
      }
      if (this.asyncQueue.length == 0) {
        return;
      }
      this.asyncQueueRunning = true;
      const next = this.asyncQueue.shift();
      if (!next) {
        this.asyncQueueRunning = false;
        this.runQueueIfNeeded();
        return;
      }
      next().catch((e) => {
        console.error("Error in async queue", e);
      }).finally(() => {
        this.asyncQueueRunning = false;
        this.runQueueIfNeeded();
      });
    },
    freezeSize() {
      const el = this.$el;
      if (!el) {
        return;
      }
      this.cancelContentAnimation();
      const w = el.offsetWidth;
      const h2 = el.offsetHeight;
      el.style.width = w + "px";
      el.style.height = h2 + "px";
      this.setFrozenHeightVariable(h2);
      const token = this.contentAnimationToken;
      setTimeout(() => {
        if (token === this.contentAnimationToken && this.hasContent() && !this.animatingContent && this.isSizeFrozen()) {
          console.error("Navigation controller size still frozen!", this);
          this.unfreezeSize();
        }
      }, Math.max(1e3, this.getNavigationTransitionDuration() + 100));
    },
    growSize(width, height) {
      const el = this.$el;
      el.style.height = height + "px";
      el.style.width = width + "px";
      this.setFrozenHeightVariable(height);
    },
    unfreezeSize() {
      const el = this.$el;
      el.style.width = "";
      el.style.height = "";
    },
    /**
     * Expose the height this controller is (or was last) pinned to as a CSS variable. Inside a
     * dynamically sized container (e.g. a sheet) the global --vh resolves to the full viewport,
     * which is wrong for placeholder/loading views that want to fill the actual (much smaller)
     * size. Those views can read var(--frozen-height) instead. We use a dedicated variable
     * rather than overriding --vh, because --vh is also used for max-height caps and overriding
     * it there would clamp incoming taller views to the old height.
     */
    setFrozenHeightVariable(height) {
      this.$el.style.setProperty("--frozen-height", height + "px");
    },
    isSizeFrozen() {
      const el = this.$el;
      return !!el && el.style.height !== "";
    },
    hasContent() {
      return !(this.mainComponent !== null && this.contentlessMainKey === this.mainComponent.key);
    },
    cancelContentAnimation() {
      var _a;
      this.contentAnimationToken++;
      this.animatingContent = false;
      (_a = this.contentTransitionCleanup) == null ? void 0 : _a.call(this);
      this.contentTransitionCleanup = null;
    },
    /**
     * Called by a (descendant) component to signal whether it currently renders content.
     * This uses an opt-out model: components are assumed to have content unless they signal
     * otherwise. A component without content (e.g. an async component that is still loading)
     * keeps the navigation controller from collapsing its frozen height to the height of the
     * empty placeholder. Once the content arrives, the controller animates to the real height.
     */
    setContentState(component, hasContent) {
      const hadContent = this.hasContent();
      if (!hasContent) {
        if (!hadContent && this.isSizeFrozen()) ;
        else {
          this.contentlessMainKey = component.key;
          if (!this.isSizeFrozen()) {
            this.freezeSize();
          }
        }
      } else if (!hadContent) {
        this.contentlessMainKey = null;
        this.animateToContentHeight();
      }
      const hasContentNow = this.hasContent();
      if (hadContent !== hasContentNow) {
        this.propagateContentState(hasContentNow);
      }
    },
    propagateContentState(hasContent) {
      const parent = this.navigationController;
      const ownComponent = this.navigation_currentComponent;
      if (parent && ownComponent && typeof parent.setContentState === "function") {
        parent.setContentState(ownComponent, hasContent);
      }
    },
    resetContentState() {
      const wasContentless = this.contentlessMainKey !== null;
      this.contentlessMainKey = null;
      if (wasContentless) {
        this.propagateContentState(true);
      }
    },
    /**
     * Animate the (still frozen) size towards the real height of the current main component.
     * Used when async content arrives after the enter animation already measured an empty
     * loading placeholder.
     */
    animateToContentHeight() {
      if (!this.isSizeFrozen()) {
        return;
      }
      const child = this.$refs.child;
      const framed = child == null ? void 0 : child.$el;
      const inner = framed == null ? void 0 : framed.firstElementChild;
      if (!inner) {
        this.unfreezeSize();
        return;
      }
      const token = ++this.contentAnimationToken;
      this.animatingContent = true;
      requestAnimationFrame(() => {
        var _a;
        if (token !== this.contentAnimationToken) {
          return;
        }
        this.waitForSizeTransition(token, () => {
          if (token !== this.contentAnimationToken) {
            console.info("Animation interrupted, not unfreezing size");
            return;
          }
          this.animatingContent = false;
          if (this.hasContent()) {
            this.unfreezeSize();
          }
        });
        const w = ((_a = inner.firstElementChild) == null ? void 0 : _a.offsetWidth) ?? inner.offsetWidth;
        const h2 = inner.offsetHeight;
        console.info("Grow size towards element", inner);
        this.growSize(w, h2);
      });
    },
    waitForSizeTransition(token, complete) {
      const element = this.$el;
      const pending = /* @__PURE__ */ new Set();
      let sawSizeTransition = false;
      let firstFrame = 0;
      let secondFrame = 0;
      const cleanup = () => {
        element.removeEventListener("transitionrun", onTransitionRun);
        element.removeEventListener("transitionend", onTransitionFinished);
        element.removeEventListener("transitioncancel", onTransitionFinished);
        cancelAnimationFrame(firstFrame);
        cancelAnimationFrame(secondFrame);
        if (this.contentTransitionCleanup === cleanup) {
          this.contentTransitionCleanup = null;
        }
      };
      const finish = () => {
        cleanup();
        complete();
      };
      const isSizeTransition = (event) => {
        return event.target === element && (event.propertyName === "height" || event.propertyName === "width");
      };
      const onTransitionRun = (event) => {
        if (!isSizeTransition(event)) {
          return;
        }
        sawSizeTransition = true;
        pending.add(event.propertyName);
      };
      const onTransitionFinished = (event) => {
        if (!isSizeTransition(event)) {
          return;
        }
        pending.delete(event.propertyName);
        if (sawSizeTransition && pending.size === 0) {
          finish();
        }
      };
      element.addEventListener("transitionrun", onTransitionRun);
      element.addEventListener("transitionend", onTransitionFinished);
      element.addEventListener("transitioncancel", onTransitionFinished);
      this.contentTransitionCleanup = cleanup;
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          if (token === this.contentAnimationToken && !sawSizeTransition) {
            finish();
          }
        });
      });
    },
    /**
     * Animate the controller's height around an in-place content change (e.g. an async
     * sub-component of the current main component finished loading). Freezes the current height,
     * applies the change, then animates to the new height. Unlike the contentless mechanism this
     * does not wait for a navigation: it captures the height as it is right now.
     */
    async animateHeightChange(applyChange) {
      if (!this.shouldAnimate()) {
        await applyChange();
        return;
      }
      this.freezeSize();
      await applyChange();
      await this.$nextTick();
      this.animateToContentHeight();
    },
    getInternalScrollElement(element = null) {
      var _a;
      const mightBe = (_a = element ?? this.$el) == null ? void 0 : _a.querySelector("main");
      return mightBe ? mightBe : null;
    },
    getScrollElement(_ = null) {
      return document.documentElement;
    },
    shouldAnimate() {
      return !this.disableAnimations && this.$el && this.$el.offsetWidth <= 1e3 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    },
    getNavigationTransitionDuration() {
      const isPop = this.transitionName === "pop" || this.transitionName === "modal-pop";
      const property = isPop ? "--navigation-pop-transition-duration" : "--navigation-transition-duration";
      const fallback = isPop ? 250 : 300;
      const value = getComputedStyle(this.$el).getPropertyValue(property).trim();
      if (value.endsWith("ms")) {
        const duration = Number.parseFloat(value);
        return Number.isFinite(duration) ? duration : fallback;
      }
      if (value.endsWith("s")) {
        const duration = Number.parseFloat(value) * 1e3;
        return Number.isFinite(duration) ? duration : fallback;
      }
      return fallback;
    },
    returnToHistoryIndex() {
      const lastComponent = this.components[this.components.length - 1];
      if (lastComponent && lastComponent.hasHistoryIndex()) {
        return lastComponent.returnToHistoryIndex();
      }
      return false;
    },
    async push(options) {
      return await this.runQueue(async () => {
        var _a;
        if (options.components.length === 0) {
          console.error("Missing component when pushing");
          return;
        }
        (_a = document.activeElement) == null ? void 0 : _a.blur();
        const components = options.components;
        const component = components[components.length - 1];
        const destroy = options.destroy ?? true;
        const force = options.force ?? false;
        const animated = this.shouldAnimate() ? options.animated === void 0 ? component.animated : options.animated : false;
        let replace = options.replace ?? 0;
        if (replace > this.components.length) {
          replace = this.components.length;
        }
        if (ComponentWithProperties.debug) console.log("Pushing new component on navigation controller: " + component.component.name);
        if (replace > 0) {
          if (destroy && !force) {
            for (let index = this.components.length - 1; index >= this.components.length - replace; index--) {
              const component2 = this.components[index];
              const r = await component2.shouldNavigateAway();
              if (!r) {
                return;
              }
            }
          }
        }
        if (!animated) {
          this.cancelContentAnimation();
          this.unfreezeSize();
          this.transitionName = "none";
        } else {
          this.transitionName = this.animationType === "modal" ? "modal-push" : options.reverse ? "pop" : "push";
        }
        const internalScrollElement = this.getInternalScrollElement();
        const internalClientHeight = internalScrollElement == null ? void 0 : internalScrollElement.clientHeight;
        this.savedInternalScrollPositions.push(((internalScrollElement == null ? void 0 : internalScrollElement.scrollTop) ?? 0) + (internalClientHeight ?? 0));
        this.nextInternalScrollPosition = 0;
        if (animated) {
          this.freezeSize();
        }
        let popped = [];
        let forceOneAdjust = false;
        const adjustHistory = (options == null ? void 0 : options.adjustHistory) ?? true;
        if (replace > 0) {
          popped = this.components.splice(this.components.length - replace, replace, ...components);
          if (!destroy) {
            for (const comp of popped) {
              comp.keepAlive = true;
            }
          }
          if (this.components.length <= components.length) {
            const lastComponent = popped[0];
            if (HistoryManager.active) {
              if (lastComponent && lastComponent.hasHistoryIndex()) {
                HistoryManager.returnToHistoryIndex(lastComponent.historyIndex.index - 1);
                forceOneAdjust = true;
              } else {
                console.log("Last removed component has no history index", popped);
                HistoryManager.invalidateHistory();
              }
            }
          } else {
            const lastComponent = this.components[this.components.length - components.length - 1];
            if (lastComponent && lastComponent.hasHistoryIndex()) {
              lastComponent.returnToHistoryIndex();
              forceOneAdjust = true;
            } else {
              console.log("Last visible component has no history index", lastComponent);
              HistoryManager.invalidateHistory();
            }
          }
        } else {
          this.components.push(...components);
        }
        if (this.mainComponent) {
          this.mainComponent.keepAlive = !replace || !destroy;
        }
        for (const [index, component2] of components.entries()) {
          if (index === 0 && popped.length && adjustHistory) {
            HistoryManager.pushState(null, async (canAnimate) => {
              if (!this.mainComponent) {
                console.error("Tried to pop NavigationController, but it was already unmounted");
                return;
              }
              await this.push({
                animated: animated && canAnimate,
                replace: 1,
                components: popped,
                reverse: !(options.reverse ?? false),
                adjustHistory: false
              });
            }, {
              adjustHistory: adjustHistory || forceOneAdjust,
              invalid: options.invalidHistory ?? false
            });
            forceOneAdjust = false;
          } else {
            HistoryManager.pushState(null, async (canAnimate) => {
              if (!this.mainComponent) {
                console.error("Tried to pop NavigationController, but it was already unmounted");
                return;
              }
              await this.pop({ animated: animated && canAnimate });
            }, {
              adjustHistory: adjustHistory || forceOneAdjust,
              invalid: options.invalidHistory ?? false
            });
          }
          forceOneAdjust = false;
          component2.assignHistoryIndex();
        }
        this.mainComponent = component;
        this.$emit("didPush");
        await this.$nextTick();
      });
    },
    async shouldNavigateAway() {
      for (let index = this.components.length - 1; index >= 0; index--) {
        const component = this.components[index];
        const r = await component.shouldNavigateAway();
        if (!r) {
          return false;
        }
      }
      return true;
    },
    popToRoot(options = {}) {
      options.count = this.components.length - 1;
      return this.pop(options);
    },
    getPoppableParent() {
      let prev = this.$;
      let start = this.$.parent;
      while (start) {
        if (prev.props.onPop) {
          return prev;
        }
        prev = start;
        start = start.parent;
      }
      return null;
    },
    async pop(options = {}) {
      return await this.runQueue(async () => {
        var _a;
        (_a = document.activeElement) == null ? void 0 : _a.blur();
        const animated = this.shouldAnimate() ? options.animated ?? true : false;
        const destroy = options.destroy ?? true;
        const count = options.count ?? 1;
        const force = options.force ?? false;
        if (this.components.length <= count) {
          const parentPop = unref(this.reactive_navigation_pop);
          options.count = 1;
          if (!parentPop) {
            console.error("Tried to pop an empty navigation controller, but couldn't find a parent to pop");
            return;
          }
          return await parentPop(options);
        }
        if (count === 0) {
          return;
        }
        if (destroy && !force) {
          for (let index = this.components.length - 1; index >= this.components.length - count; index--) {
            const component = this.components[index];
            const r = await component.shouldNavigateAway();
            if (!r) {
              return;
            }
          }
        }
        if (!animated) {
          this.cancelContentAnimation();
          this.unfreezeSize();
          this.transitionName = "none";
        } else {
          this.transitionName = this.animationType === "modal" ? "modal-pop" : "pop";
          this.freezeSize();
        }
        const popped = this.components.splice(this.components.length - count, count);
        if (!destroy) {
          for (const comp of popped) {
            comp.keepAlive = true;
          }
        }
        this.nextInternalScrollPosition = Math.max(0, this.savedInternalScrollPositions.pop() ?? 0);
        this.mainComponent = this.components[this.components.length - 1];
        this.mainComponent.returnToHistoryIndex();
        await this.$nextTick();
        this.$emit("didPop");
        return popped;
      });
    },
    beforeEnter(insertedElement) {
      if (this.transitionName == "none") {
        return;
      }
      insertedElement.className = this.transitionName + "-enter-active " + this.transitionName + "-enter-from";
    },
    beforeLeave(leavingElement) {
      if (this.transitionName == "none") {
        return;
      }
      leavingElement.className = this.transitionName + "-leave-active ";
    },
    beforeBeforeEnterAnimation() {
      if (this.mainComponent) {
        const instance = this.mainComponent.componentInstance();
        if (instance && instance.beforeBeforeEnterAnimation) {
          instance.beforeBeforeEnterAnimation();
        }
      }
    },
    finishedEnterAnimation() {
      if (this.mainComponent) {
        const instance = this.mainComponent.componentInstance();
        if (instance && instance.finishedEnterAnimation) {
          instance.finishedEnterAnimation();
        }
      }
    },
    enter(element, done) {
      if (this.transitionName === "none") {
        const internal = this.getInternalScrollElement(element);
        if (internal) {
          internal.scrollTop = Math.max(0, this.nextInternalScrollPosition - internal.clientHeight);
        }
        done();
        return;
      }
      requestAnimationFrame(() => {
        const w = element.firstElementChild.firstElementChild.offsetWidth;
        const h2 = element.firstElementChild.offsetHeight;
        const internal = this.getInternalScrollElement(element);
        let nextInternal = this.nextInternalScrollPosition;
        if (internal) {
          nextInternal = Math.max(0, this.nextInternalScrollPosition - internal.clientHeight);
          const scrollOuterHeight = this.getScrollOuterHeight(internal);
          const h22 = internal.scrollHeight;
          if (nextInternal > h22 - scrollOuterHeight) {
            nextInternal = Math.max(0, h22 - scrollOuterHeight);
          }
        }
        const childElement = element.firstElementChild;
        const transitionDuration = this.getNavigationTransitionDuration();
        if (this.transitionName == "push" || this.transitionName == "pop" || this.transitionName == "modal-push") {
          childElement.style.willChange = "transform";
        }
        if (internal) {
          internal.style.willChange = "scroll-position";
        }
        if (this.hasContent()) {
          this.growSize(w, h2);
        }
        requestAnimationFrame(() => {
          if (internal) {
            internal.scrollTop = nextInternal;
          }
          element.className = this.transitionName + "-enter-active " + this.transitionName + "-enter-to";
          setTimeout(() => {
            element.style.willChange = "";
            childElement.style.willChange = "";
            if (internal) {
              internal.style.willChange = "";
            }
            done();
          }, transitionDuration + 25);
        });
      });
    },
    getScrollOuterHeight(scrollElement) {
      let h2 = scrollElement.clientHeight;
      if (scrollElement === document.documentElement) {
        const w = window;
        if (w.visualViewport) {
          h2 = w.visualViewport.height;
        }
      }
      return h2;
    },
    leave(element, done) {
      if (this.transitionName == "none") {
        done();
        return;
      }
      const childElement = element.firstElementChild;
      childElement.style.willChange = "transform";
      const transitionDuration = this.getNavigationTransitionDuration();
      requestAnimationFrame(() => {
        const h2 = this.$el.offsetHeight;
        const w = this.$el.offsetWidth;
        const height = h2 + "px";
        const width = w + "px";
        element.className = this.transitionName + "-leave-active " + this.transitionName + "-leave-from";
        element.style.top = "0px";
        element.style.height = height;
        element.style.width = width;
        element.style.bottom = "auto";
        element.style.overflow = "hidden";
        childElement.style.overflow = "hidden";
        childElement.style.height = height;
        childElement.style.width = width;
        requestAnimationFrame(() => {
          element.className = this.transitionName + "-leave-active " + this.transitionName + "-leave-to";
          setTimeout(() => {
            element.style.overflow = "";
            element.style.top = "";
            element.style.height = "";
            element.style.bottom = "";
            childElement.style.overflow = "";
            childElement.style.willChange = "";
            done();
          }, transitionDuration + 25);
        });
      });
    },
    afterLeave(element) {
      if (this.transitionName == "none") {
        return;
      }
      element.className = "";
    },
    afterEnter(element) {
      if (this.transitionName == "none") {
        return;
      }
      element.className = "";
      if (!this.hasContent() || this.animatingContent) {
        return;
      }
      this.unfreezeSize();
    },
    enterCancelled(_element) {
      if (!this.hasContent() || this.animatingContent) {
        return;
      }
      this.unfreezeSize();
    }
  }
});
function wrapWithNavigationController(component) {
  if (component.component === NavigationController$1) {
    return component;
  }
  return new ComponentWithProperties(NavigationController$1, {
    root: component
  });
}
const _hoisted_1$4 = {
  key: 0,
  class: "navigation-controller"
};
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_FramedComponent = resolveComponent("FramedComponent");
  return _ctx.mainComponent ? (openBlock(), createElementBlock("div", _hoisted_1$4, [
    createVNode(Transition, {
      css: false,
      onBeforeEnter: _ctx.beforeEnter,
      onBeforeLeave: _ctx.beforeLeave,
      onEnter: _ctx.enter,
      onLeave: _ctx.leave,
      onAfterLeave: _ctx.afterLeave,
      onAfterEnter: _ctx.afterEnter,
      onEnterCancelled: _ctx.enterCancelled
    }, {
      default: withCtx(() => [
        (openBlock(), createBlock(_component_FramedComponent, {
          key: _ctx.mainComponent.key,
          ref: "child",
          root: _ctx.mainComponent,
          "custom-provide": _ctx.provideForComponent(_ctx.mainComponent.key)
        }, null, 8, ["root", "custom-provide"]))
      ]),
      _: 1
    }, 8, ["onBeforeEnter", "onBeforeLeave", "onEnter", "onLeave", "onAfterLeave", "onAfterEnter", "onEnterCancelled"])
  ])) : createCommentVNode("", true);
}
const NavigationController = /* @__PURE__ */ _export_sfc(NavigationController$1, [["render", _sfc_render$1]]);
const _UrlHelper = class _UrlHelper {
  constructor(url, localFixedPrefix) {
    __publicField(this, "url");
    __publicField(this, "localFixedPrefix");
    this.url = new URL(url ?? window.location.href);
    this.localFixedPrefix = localFixedPrefix ?? null;
  }
  get path() {
    return this.url.pathname;
  }
  get hash() {
    return this.url.hash;
  }
  get href() {
    return this.url.href;
  }
  get fullPrefix() {
    let prefix = _UrlHelper.fixedPrefix ? _UrlHelper.trim(_UrlHelper.fixedPrefix) : null;
    if (this.localFixedPrefix) {
      if (prefix) {
        prefix += "/" + _UrlHelper.trim(this.localFixedPrefix);
      } else {
        prefix = _UrlHelper.trim(this.localFixedPrefix);
      }
    }
    return prefix;
  }
  setPath(path) {
    this.url.pathname = path;
  }
  setDomain(domain, protocol = "https") {
    this.url.host = domain;
    this.url.protocol = protocol;
  }
  /**
   * Get full path, with the locale removed by default
   * /your-path/test?q=t#hash
   */
  getPath(options) {
    const search = new URL(this.href ?? "/", "https://" + window.location.hostname).search;
    return "/" + this.getParts(options).join("/") + search + this.hash;
  }
  getHostWithProtocol() {
    const url = new URL(this.href ?? "/", "https://" + window.location.hostname);
    return url.protocol + "//" + url.host;
  }
  getFullHref(options) {
    const url = new URL(this.href ?? "/", "https://" + window.location.hostname);
    return url.protocol + "//" + ((options == null ? void 0 : options.host) ?? url.host) + this.getPath(options);
  }
  getParts(options) {
    var _a;
    const parts = ((_a = this.path) == null ? void 0 : _a.substring(1).split("/")) ?? [];
    if (parts.length > 0 && parts[parts.length - 1] === "") {
      parts.pop();
    }
    if (parts.length > 0 && ((options == null ? void 0 : options.removeLocale) === void 0 || (options == null ? void 0 : options.removeLocale) === true) && (parts[0].length == 5 && parts[0].substring(2, 3) === "-" || parts[0].length == 2)) {
      parts.shift();
    }
    if (((options == null ? void 0 : options.removePrefix) === void 0 || (options == null ? void 0 : options.removePrefix) === true) && this.fullPrefix) {
      for (const part of this.fullPrefix.split("/") ?? []) {
        if (parts.length > 0 && parts[0] === part) {
          parts.shift();
        } else {
          break;
        }
      }
    }
    if (options == null ? void 0 : options.appendPrefix) {
      parts.unshift(options.appendPrefix);
    }
    return parts;
  }
  getSearchParams() {
    return this.url.searchParams;
  }
  getHashParams() {
    var _a;
    return new URLSearchParams(
      ((_a = this.hash) == null ? void 0 : _a.substr(1)) ?? ""
      // skip the first char (#)
    );
  }
  clear() {
    this.url = new URL("/", "https://" + window.location.hostname);
  }
  /**
   * override params
   */
  static setSearchParams(params) {
    const helper = new _UrlHelper();
    const url = new URL(helper.getFullHref());
    url.search = params.toString();
    this.setUrl(url.pathname + url.search + url.hash);
  }
  /**
   * override params
   */
  static addSearchParam(key, value) {
    const helper = new _UrlHelper();
    const url = new URL(helper.getFullHref());
    url.searchParams.set(key, value);
    url.search = url.searchParams.toString();
    this.setUrl(url.pathname + url.search + url.hash);
  }
  /**
   * Return a transformed url (adds locale and fixed prefix to it)
   */
  static transformUrl(url, localFixedPrefix) {
    let prefix = this.fixedPrefix ? "/" + _UrlHelper.trim(this.fixedPrefix) : "";
    if (localFixedPrefix) {
      prefix += "/" + _UrlHelper.trim(localFixedPrefix);
    }
    if (this.localePrefix) {
      return "/" + _UrlHelper.trim(this.localePrefix) + prefix + "/" + _UrlHelper.trim(url);
    }
    return prefix + "/" + _UrlHelper.trim(url);
  }
  /**
   * @deprecated
   * setURL, but add locale
   */
  static setUrl(url) {
    console.warn("Used UrlHelper.setUrl", url, "which should be replaced with this.setUrl()");
  }
  static trim(url) {
    return url.replace(/^\/+/, "").replace(/\/+$/, "");
  }
  match(template, params) {
    return matchPath(this.getParts(), this.getSearchParams(), template, params);
  }
};
/**
 * Use this for the universal fixed prefix
 *
 * Always remove this prefix when getting an url, and add it when doing setUrl.
 * When you want to host an app in a subdirectory
 * Slashes are added automatically on the sides if needed
*/
__publicField(_UrlHelper, "fixedPrefix", null);
__publicField(_UrlHelper, "localePrefix", null);
__publicField(_UrlHelper, "shared", new _UrlHelper(window.location.href));
/**
 * The original values when loading the page. Do not modify this one.
 */
__publicField(_UrlHelper, "initial", new _UrlHelper(window.location.href));
let UrlHelper = _UrlHelper;
function mergeSearchParams(a, b) {
  if (a === null && b === null) {
    return null;
  }
  if (a === null) {
    return new URLSearchParams(b);
  }
  if (b === null) {
    return new URLSearchParams(a);
  }
  const merged = new URLSearchParams(a);
  for (const [key, value] of b.entries()) {
    merged.append(key, value);
  }
  return merged;
}
function matchPath(path, query, template, params) {
  if (template === "") {
    return {
      params: {},
      url: "",
      query
    };
  }
  const parts = Array.isArray(path) ? path : UrlHelper.trim(path).split("/");
  const templateParts = UrlHelper.trim(template).split("/");
  if (parts.length < templateParts.length) {
    return;
  }
  const resultParams = {};
  for (let index = 0; index < templateParts.length; index++) {
    const templatePart = templateParts[index];
    const part = parts[index];
    if (templatePart !== part) {
      if (templatePart[0] === "@") {
        const param = templatePart.substr(1);
        if (params[param]) {
          resultParams[param] = params[param](part);
          if (typeof resultParams[param] === "number") {
            if (!Number.isInteger(resultParams[param])) {
              return;
            }
          }
          continue;
        }
      }
      return;
    }
  }
  return {
    params: resultParams,
    url: parts.slice(0, templateParts.length).join("/"),
    query
  };
}
function matchQuery() {
}
function templateToUrl(template, params) {
  const templateParts = UrlHelper.trim(template).split("/");
  for (let index = 0; index < templateParts.length; index++) {
    const templatePart = templateParts[index];
    if (templatePart[0] === "@") {
      const param = templatePart.substr(1);
      if (params[param]) {
        templateParts[index] = params[param].toString();
      } else {
        throw new Error("Missing param: " + param);
      }
    }
  }
  return templateParts.slice(0, templateParts.length).join("/");
}
const KEY_READING_METHODS = /* @__PURE__ */ new Set(["get", "getAll", "has"]);
const FULL_SCAN_METHODS = /* @__PURE__ */ new Set(["entries", "keys", "values", "forEach", "toString"]);
function createTrackedSearchParams(source) {
  const target = source instanceof URLSearchParams ? source : new URLSearchParams(source);
  const readKeys = /* @__PURE__ */ new Set();
  const markAll = () => {
    for (const key of target.keys()) readKeys.add(key);
  };
  const proxy = new Proxy(target, {
    get(t, prop, _receiver) {
      if (prop === Symbol.iterator) {
        markAll();
        return t[Symbol.iterator].bind(t);
      }
      const value = Reflect.get(t, prop);
      if (typeof value !== "function") {
        return value;
      }
      const name = typeof prop === "string" ? prop : "";
      if (KEY_READING_METHODS.has(name)) {
        return (key, ...rest) => {
          readKeys.add(key);
          return value.call(t, key, ...rest);
        };
      }
      if (FULL_SCAN_METHODS.has(name)) {
        return (...args) => {
          markAll();
          return value.apply(t, args);
        };
      }
      return value.bind(t);
    }
  });
  return {
    params: proxy,
    readKeys,
    getReadKeys: () => [...readKeys],
    getUnreadKeys: () => [...target.keys()].filter((k) => !readKeys.has(k)),
    reset: () => readKeys.clear()
  };
}
class ReactiveUrl {
  constructor({ url, matched, query }) {
    __publicField(this, "url");
    __publicField(this, "query");
    /**
     * The url that actually matched vs the url that will be shown. This is used to support alternative urls
     *
     * E.g.
     * matched:
     * /members/all
     *
     * url:
     * /leden/allemaal
     *
     * When checking further the url, we need to know what we matched earlier
     */
    __publicField(this, "matched");
    this.url = url;
    this.matched = matched ?? url;
    this.query = query ?? null;
  }
  extend(url) {
    return new ReactiveUrl({
      url: this.url && this.url !== "/" ? this.url + "/" + UrlHelper.trim(url.url) : UrlHelper.trim(url.url),
      matched: this.matched && this.matched !== "/" ? this.matched + "/" + UrlHelper.trim(url.matched) : UrlHelper.trim(url.matched),
      query: mergeSearchParams(this.query, url.query)
    });
  }
  toString() {
    return this.transformedHref;
  }
  get transformedHref() {
    return "/" + UrlHelper.trim(UrlHelper.transformUrl(this.url)) + (this.query && this.query.size > 0 ? "?" + this.query.toString() : "");
  }
}
function isPromiseLike(value) {
  return value !== null && (typeof value === "object" || typeof value === "function") && typeof value.then === "function";
}
function typeRoute(route) {
  return route;
}
const defineRoute = (route) => {
  defineRoutes([route]);
  return route;
};
function usePop() {
  const rawPop = inject("reactive_navigation_pop", null);
  return (options) => {
    const pop = unref(rawPop);
    if (!pop) {
      console.warn("Failed to perform pop");
      return;
    }
    return pop(options);
  };
}
function useNavigate() {
  const instance = getCurrentInstance();
  const present = usePresent();
  const show = useShow();
  const showDetail = useShowDetail();
  const toRoute = async function(route, options) {
    let componentProperties = (options == null ? void 0 : options.properties) ?? ("defaultProperties" in route && route.defaultProperties ? route.defaultProperties((options == null ? void 0 : options.query) ?? null) : null) ?? {};
    let params = (options == null ? void 0 : options.params) ?? {};
    let query = (options == null ? void 0 : options.query) ?? null;
    if (!(options == null ? void 0 : options.properties)) {
      if ("paramsToProps" in route) {
        let onResolve = function(r) {
          if (trackedQuery) {
            const keys = trackedQuery.getReadKeys();
            if (keys.length) {
              const query2 = new URLSearchParams();
              for (const key of keys) {
                query2.set(key, options.query.get(key) ?? "");
              }
              reactiveQuery.value = query2;
            } else {
              reactiveQuery.value = null;
            }
          }
          return r;
        };
        const reactiveQuery = ref(null);
        query = reactiveQuery;
        const trackedQuery = (options == null ? void 0 : options.query) ? createTrackedSearchParams(options.query) : null;
        componentProperties = route.paramsToProps((options == null ? void 0 : options.params) ?? {}, (trackedQuery == null ? void 0 : trackedQuery.params) ?? null);
        if (isPromiseLike(componentProperties)) {
          componentProperties = componentProperties.then(onResolve);
        } else {
          onResolve(void 0);
        }
      } else {
        query = null;
        if (options == null ? void 0 : options.params) {
          console.error("Using route to a route that only has properties, no parameters. Use properties instead of params in navigate() for this route: " + route.url, route, options);
        }
      }
    }
    if (!(options == null ? void 0 : options.params) && "propsToParams" in route && route.propsToParams) {
      const { params: p, query: q } = route.propsToParams(await componentProperties);
      params = p ?? {};
      query = q ?? null;
    }
    const url = isRef(query) ? computed(() => {
      return new ReactiveUrl({
        url: templateToUrl(route.url, params ?? {}),
        matched: (options == null ? void 0 : options.url) ? templateToUrl(options == null ? void 0 : options.url, params ?? {}) : options == null ? void 0 : options.url,
        query: unref(query)
      });
    }) : new ReactiveUrl({
      url: templateToUrl(route.url, params ?? {}),
      matched: (options == null ? void 0 : options.url) ? templateToUrl(options == null ? void 0 : options.url, params ?? {}) : options == null ? void 0 : options.url,
      query
    });
    if ("handler" in route) {
      await route.handler({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        modalDisplayStyle: void 0,
        // modalDisplayStyle: route.present && typeof route.present === 'string' ? route.present : undefined,
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false,
        componentProperties: await componentProperties
      });
      return;
    }
    let component;
    const isComponentFunction = typeof route.component === "function" && !(!!route.component.prototype && route.component.prototype.constructor === route.component);
    if (isComponentFunction || componentProperties.then) {
      const method = isComponentFunction ? route.component : () => Promise.resolve(route.component);
      const originalProperties = componentProperties;
      if (!("PromiseComponent" in window)) {
        throw new Error("Required PromiseComponent window variable to make async components work in routes");
      }
      component = window.PromiseComponent;
      const importWithRetry = "_importWithRetry" in window ? window._importWithRetry : (a, args) => a(...args);
      componentProperties = {
        promise: async () => {
          const properties = await originalProperties;
          const realComponent = await importWithRetry(method, [properties]);
          if (realComponent instanceof ComponentWithProperties) {
            return realComponent;
          }
          return new ComponentWithProperties(realComponent === "self" ? instance == null ? void 0 : instance.type : realComponent, properties);
        }
      };
    } else {
      component = route.component === "self" ? instance == null ? void 0 : instance.type : route.component;
    }
    if (!component) {
      throw new Error("Component not found");
    }
    if (route.present) {
      await present({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        components: [
          new ComponentWithProperties(NavigationController, {
            root: new ComponentWithProperties(component, componentProperties)
          })
        ],
        modalDisplayStyle: typeof route.present === "string" ? route.present : void 0,
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false,
        replace: route.replace,
        force: route.force
      });
    } else if (route.show === "detail") {
      await showDetail({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        components: [
          new ComponentWithProperties(NavigationController, {
            root: new ComponentWithProperties(component, componentProperties)
          })
        ],
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false,
        replace: route.replace,
        force: route.force
      });
    } else {
      await show({
        url,
        adjustHistory: (options == null ? void 0 : options.adjustHistory) ?? true,
        animated: (options == null ? void 0 : options.animated) ?? true,
        components: [
          new ComponentWithProperties(component, componentProperties)
        ],
        checkRoutes: (options == null ? void 0 : options.checkRoutes) ?? false,
        replace: route.replace,
        force: route.force
      }, typeof route.show === "object" && route.show !== null && "getCustomShow" in route.show ? route.show.getCustomShow() : void 0);
    }
  };
  const currentRoutes = getCurrentRoutes();
  const toId = async function(urlOrName, options) {
    const route = currentRoutes.value.find((r) => r.name === urlOrName || r.url === urlOrName);
    if (!route) {
      throw new Error("Route " + urlOrName + " not found in " + (instance == null ? void 0 : instance.type.name));
    }
    return await toRoute(route, options);
  };
  return async function(prop1, prop2) {
    if (typeof prop1 === "string") {
      return await toId(prop1, prop2);
    }
    await toRoute(prop1, prop2);
  };
}
function useInheritCheckRoutes() {
  const component = useCurrentComponent();
  const parent = useParentComponent();
  if (component && parent) {
    if (parent.checkRoutes) {
      component.setCheckRoutes();
    }
  }
}
function getCurrentRoutes() {
  const component = useCurrentComponent();
  return customRef(() => {
    return {
      get() {
        return (component == null ? void 0 : component.navigationRoutes) ?? [];
      },
      set(newValue) {
        if (component) {
          component.navigationRoutes.splice(0, component.navigationRoutes.length, ...newValue);
        } else {
          console.error("Missing component when setting routes");
        }
      }
    };
  });
}
function onCheckRoutes(handler) {
  const instance = getCurrentInstance();
  instance._navigationCheckRoutesHandlers = [...instance._navigationCheckRoutesHandlers ?? [], handler];
  addCheckRoutesMountedHandler();
}
function onNotCheckRoutes(handler) {
  const instance = getCurrentInstance();
  instance._navigationNotCheckRoutesHandlers = [...instance._navigationNotCheckRoutesHandlers ?? [], handler];
  addCheckRoutesMountedHandler();
}
function addCheckRoutesMountedHandler() {
  const instance = getCurrentInstance();
  const component = useCurrentComponent();
  if (instance._didAddCheckRoutesMountedHandler) {
    return;
  }
  instance._didAddCheckRoutesMountedHandler = true;
  let doDeactivate = false;
  onBeforeUnmount(() => {
    if (component && doDeactivate) {
      component.checkRoutes = false;
    }
  });
  onMounted(async () => {
    if (component && component.checkRoutes) {
      doDeactivate = true;
      if ("_navigationCheckRoutesHandlers" in instance && Array.isArray(instance._navigationCheckRoutesHandlers)) {
        for (const handler of instance._navigationCheckRoutesHandlers) {
          if (typeof handler === "function") {
            if (await handler() === true) {
              return;
            }
          } else {
            console.error("Invalid checkRoutes handler", handler);
          }
        }
      }
    } else {
      if ("_navigationNotCheckRoutesHandlers" in instance && Array.isArray(instance._navigationNotCheckRoutesHandlers)) {
        for (const handler of instance._navigationNotCheckRoutesHandlers) {
          if (typeof handler === "function") {
            if (await handler() === true) {
              return;
            }
          } else {
            console.error("Invalid not checkRoutes handler", handler);
          }
        }
      }
    }
  });
}
function defineRoutes(tmpRoutes) {
  const urlhelpers = useUrl();
  const navigate = useNavigate();
  const currentRoutes = getCurrentRoutes();
  const provideDefaultHandler = inject("reactive_provide_default_handler", null);
  let hadRoutes = false;
  if (currentRoutes.value.length) {
    hadRoutes = true;
    tmpRoutes = [...currentRoutes.value, ...tmpRoutes];
  }
  currentRoutes.value = tmpRoutes;
  if (hadRoutes || tmpRoutes.length === 0) {
    return;
  }
  async function handleRoutes(routes) {
    for (const route of routes) {
      let result;
      for (const url of [route.url, ...route.alternativeUrls ?? []]) {
        result = urlhelpers.match(url, "params" in route ? route.params : {});
        if (result) {
          break;
        }
      }
      if (result) {
        let ignore = false;
        for (const ignoreUrl of route.ignoreUrls ?? []) {
          const doesMatchIgnore = urlhelpers.match(ignoreUrl, "params" in route ? route.params : {});
          if (doesMatchIgnore) {
            ignore = true;
            break;
          }
        }
        if (ignore) {
          continue;
        }
        try {
          await navigate(route, {
            url: result.url,
            params: "params" in route ? result.params : void 0,
            animated: false,
            adjustHistory: false,
            query: result.query,
            checkRoutes: true
          });
          return true;
        } catch (e) {
          console.error("Failed to navigate to route", route, e);
        }
      }
    }
    try {
      if (await defaultHandler({ allowDetail: false })) {
        return true;
      }
    } catch (e) {
      console.error("Failed to navigate to default (non-detail) route", e);
    }
    return false;
  }
  const getDefaultRoute = ({ allowDetail } = { allowDetail: true }) => {
    const routes = currentRoutes.value;
    if (!Array.isArray(routes)) {
      return null;
    }
    return routes.find((route) => route.isDefault && (allowDetail || !("show" in route) || route.show !== "detail")) ?? null;
  };
  const defaultHandler = async ({ allowDetail } = { allowDetail: true }) => {
    const defaultRoute = getDefaultRoute({ allowDetail });
    if (defaultRoute) {
      try {
        await navigate(defaultRoute, {
          ...defaultRoute.isDefault,
          adjustHistory: false,
          checkRoutes: false
        });
        return true;
      } catch (e) {
        console.error("Failed to navigate to default route", defaultRoute, e);
      }
    }
    return false;
  };
  const setDefaultHandler = () => {
    const dr = getDefaultRoute();
    if (dr) {
      const unreffedProvideDefaultHandler = unref(provideDefaultHandler);
      if (unreffedProvideDefaultHandler) {
        unreffedProvideDefaultHandler(defaultHandler);
      }
    }
  };
  onCheckRoutes(async () => {
    const routes = currentRoutes.value;
    if (await handleRoutes(routes)) {
      setDefaultHandler();
      return;
    }
    setDefaultHandler();
  });
  onNotCheckRoutes(async () => {
    await defaultHandler({ allowDetail: false });
    setDefaultHandler();
  });
}
function useCurrentHref() {
  const state = ref(window.location.href);
  const owner = {};
  HistoryManager.addListener(owner, () => {
    state.value = window.location.href;
  });
  onScopeDispose(() => {
    HistoryManager.removeListener(owner);
  });
  return state;
}
function useCheckRoute() {
  const urlhelpers = useUrl();
  const currentRoutes = getCurrentRoutes();
  const instance = getCurrentInstance();
  const currentPath = useCurrentHref();
  const checkRouteCache = {
    lastUrl: null,
    results: /* @__PURE__ */ new Map()
  };
  const checkMatchResult = function(route, result, options) {
    if (!result) {
      return false;
    }
    if (options) {
      for (const key in options.params) {
        if (result.params[key] !== options.params[key]) {
          return false;
        }
      }
      for (const key in options.query) {
        if (result.query.get(key) !== options.query.get(key)) {
          return false;
        }
      }
      if (!(options == null ? void 0 : options.params) && "propsToParams" in route && options.properties && route.propsToParams) {
        const { params, query } = route.propsToParams(options.properties);
        for (const key in params) {
          if (result.params[key] !== params[key]) {
            return false;
          }
        }
        if (query) {
          for (const key in query) {
            if (result.query.get(key) !== query.get(key)) {
              return false;
            }
          }
        }
      }
    }
    return true;
  };
  const quickCheckCache = function(id, options) {
    if (checkRouteCache.lastUrl !== currentPath.value) {
      checkRouteCache.results.clear();
      checkRouteCache.lastUrl = currentPath.value;
    } else {
      const result = checkRouteCache.results.get(id);
      if (result) {
        return checkMatchResult(result.route, result.result, options);
      }
    }
  };
  const checkRoute = function(route, options) {
    const result = urlhelpers.matchCurrent(route.url, "params" in route ? route.params : {});
    checkRouteCache.results.set(route.url, { result, route });
    if (route.name) {
      checkRouteCache.results.set(route.name, { result, route });
    }
    return checkMatchResult(route, result, options);
  };
  const checkId = function(urlOrName, options) {
    const result = quickCheckCache(urlOrName, options);
    if (result !== void 0) {
      return result;
    }
    const route = currentRoutes.value.find((r) => r.name === urlOrName || r.url === urlOrName);
    if (!route) {
      throw new Error("Route " + urlOrName + " not found in " + (instance == null ? void 0 : instance.type.name));
    }
    return checkRoute(route, options);
  };
  return function(prop1, prop2) {
    if (typeof prop1 === "string") {
      return checkId(prop1, prop2);
    }
    const result = quickCheckCache(prop1.name || prop1.url, prop2);
    if (result !== void 0) {
      return result;
    }
    return checkRoute(prop1, prop2);
  };
}
function normalizePushOptions(o, currentComponent, urlHelpers) {
  let options;
  if (!o.components) {
    options = { components: [o] };
  } else {
    options = o;
  }
  if (options.checkRoutes) {
    options.components[options.components.length - 1].setCheckRoutes();
  }
  if (currentComponent) {
    for (const component of options.components) {
      component.inheritFromDisplayer(currentComponent);
    }
  } else {
    console.warn("Using show or present outside of a component: Inherited properties will not be set.");
  }
  if (options.url !== void 0) {
    const url = options.url;
    for (const component of options.components) {
      component.provide.reactive_navigation_url = computed(() => {
        const u = unref(url);
        return u === null ? null : urlHelpers.extendUrl(typeof u === "string" ? new ReactiveUrl({ url: u }) : u, { returnHistory: options.replace ?? 0 });
      });
    }
  }
  return options;
}
function useManualPresent() {
  const currentComponent = useCurrentComponent();
  const urlHelpers = useUrl();
  return (present, options) => {
    return present(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function useShow() {
  const currentComponent = useCurrentComponent();
  const rawShow = inject("reactive_navigation_show", null);
  const urlHelpers = useUrl();
  return (options, customShow) => {
    const show = customShow ?? unref(rawShow);
    if (!show) {
      console.warn("Failed to perform show");
      return Promise.resolve();
    }
    return show(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function useShowDetail() {
  const currentComponent = useCurrentComponent();
  const rawShowDetail = inject("reactive_navigation_show_detail", null);
  const urlHelpers = useUrl();
  return (options) => {
    const showDetail = unref(rawShowDetail);
    if (!showDetail) {
      console.warn("Failed to perform showDetail");
      return Promise.resolve();
    }
    return showDetail(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function usePresent() {
  const currentComponent = useCurrentComponent();
  const rawPresent = inject("reactive_navigation_present", null);
  const urlHelpers = useUrl();
  return async (options) => {
    const present = unref(rawPresent);
    if (!present) {
      console.warn("Failed to perform present");
      return Promise.resolve();
    }
    return present(normalizePushOptions(options, currentComponent, urlHelpers));
  };
}
function useDismiss() {
  const rawDismiss = inject("reactive_navigation_dismiss", null);
  return (options) => {
    const dismiss = unref(rawDismiss);
    if (!dismiss) {
      console.warn("Failed to perform dismiss");
      return Promise.resolve();
    }
    return dismiss(options);
  };
}
function useCanPop() {
  const rawPop = inject("reactive_navigation_can_pop", false);
  return computed(() => {
    return !!unref(rawPop);
  });
}
function useCanDismiss() {
  const rawDismiss = inject("reactive_navigation_can_dismiss", false);
  return computed(() => !!unref(rawDismiss));
}
function useFocused() {
  const rawFocused = inject("reactive_navigation_focused", true);
  return computed(() => !!unref(rawFocused));
}
function useContentState() {
  const component = useCurrentComponent();
  const controller = inject("reactive_navigationController", null);
  return (hasContent) => {
    const c = unref(controller);
    if (c && component && typeof c.setContentState === "function") {
      c.setContentState(component, hasContent);
    }
  };
}
function useAnimateHeightChange() {
  const controller = inject("reactive_navigationController", null);
  return async (applyChange) => {
    const c = unref(controller);
    if (c && typeof c.animateHeightChange === "function") {
      await c.animateHeightChange(applyChange);
      return;
    }
    await applyChange();
  };
}
function extendUrl(url) {
  const urlHelpers = useUrl();
  provide("reactive_navigation_url", computed(() => {
    const u = unref(url);
    return urlHelpers.extendUrl(typeof u === "string" ? new ReactiveUrl({ url: u }) : u);
  }));
}
function setTitleSuffix(title) {
  HistoryManager.titleSuffix = title;
}
function setTitle(title) {
  if (!title) {
    return;
  }
  const urlHelpers = useUrl();
  onActivated(() => {
    urlHelpers.setTitle(title);
  });
}
function setUrl(url, title) {
  const urlHelpers = useUrl();
  onMounted(() => {
    urlHelpers.overrideUrl(url, title);
  });
}
function useUrl() {
  const currentComponent = useCurrentComponent();
  const navigationUrl = inject("reactive_navigation_url", null);
  const navigationQuery = inject("reactive_navigation_query", null);
  const disableUrl = inject("reactive_navigation_disable_url", null);
  const historyIndex = inject("navigation_historyIndex", null);
  return {
    getUrl() {
      return unref(navigationUrl) ?? null;
    },
    getQuery() {
      return unref(navigationQuery) ?? null;
    },
    /**
     * Ideally call this after you've processed all the .match() calls (and awaited async stuff)
     */
    setTitle(title) {
      if (!currentComponent) {
        console.error("No current component while setting title", title);
        return;
      }
      if (unref(disableUrl)) {
        return;
      }
      if (title) {
        currentComponent.setTitle(title);
      }
    },
    extendUrl(url, options = {}) {
      let prefix = this.getUrl();
      if (options.returnHistory) {
        const index = unref(historyIndex);
        if (index !== null && index !== void 0) {
          prefix = HistoryManager.getStateUrl(index.index - options.returnHistory);
        } else {
          console.error("Failed to get history index");
        }
      }
      if (prefix) {
        return prefix.extend(url);
      }
      return url;
    },
    match(template, params) {
      var _a;
      const shared = UrlHelper.shared;
      const helper = new UrlHelper(shared.url, (_a = this.getUrl()) == null ? void 0 : _a.matched);
      return helper.match(template, params);
    },
    matchCurrent(template, params) {
      var _a;
      const helper = new UrlHelper(void 0, (_a = this.getUrl()) == null ? void 0 : _a.url);
      return helper.match(template, params);
    },
    overrideUrl(url, title) {
      if (!currentComponent) {
        console.error("No current component while setting title", title);
        return;
      }
      if (unref(disableUrl)) {
        return;
      }
      currentComponent == null ? void 0 : currentComponent.overrideUrl(url, title);
    }
  };
}
function awaitPrerenderingFinished() {
  if (!document.prerendering) {
    return Promise.resolve();
  }
  console.info("HistoryManager: Chrome Prerendering detected (speculative navigation). Queue is paused until prerendering is navigated upon.");
  return new Promise((resolve) => {
    document.addEventListener("prerenderingchange", () => {
      console.info("HistoryManager: Chrome Prerendering ended.");
      resolve(void 0);
    }, {
      once: true
    });
  });
}
class HistoryManagerStatic {
  constructor() {
    __publicField(this, "debug", false);
    // undoActions: Map<number, (animate: boolean) => void> = new Map();
    __publicField(this, "states", []);
    __publicField(this, "counter", 0);
    __publicField(this, "active", false);
    __publicField(this, "animateHistoryPop", true);
    __publicField(this, "isAdjustingState", false);
    __publicField(this, "manualStateAction", false);
    // Manipulating the history is async and can cause issues when fast calls happen without awaiting the previous one
    __publicField(this, "historyQueue", []);
    __publicField(this, "isQueueRunning", false);
    __publicField(this, "changeUrlTimeout", null);
    __publicField(this, "titleSuffix", "");
    __publicField(this, "pageLoadedAt", Date.now());
    __publicField(this, "listeners", /* @__PURE__ */ new Map());
  }
  addListener(owner, handler) {
    this.listeners.set(owner, handler);
  }
  removeListener(owner) {
    this.listeners.delete(owner);
  }
  callListeners() {
    for (const [_, handler] of this.listeners) {
      handler();
    }
  }
  addToQueue(action) {
    this.historyQueue.push(action);
    if (!this.isQueueRunning) {
      this.runQueue();
    }
  }
  waitForQueue() {
    return new Promise((resolve) => {
      this.addToQueue(() => {
        resolve();
      });
    });
  }
  runQueue() {
    this.isQueueRunning = true;
    const action = this.historyQueue.shift();
    if (action) {
      if (this.debug) {
        console.log("Running history queue action");
      }
      const p = action();
      if (p) {
        p.finally(() => this.runQueue()).catch(console.error);
      } else {
        this.runQueue();
      }
    } else {
      if (this.debug) {
        console.log("History queue done");
      }
      this.isQueueRunning = false;
      this.callListeners();
    }
  }
  go(delta) {
    this.addToQueue(async () => {
      await awaitPrerenderingFinished();
      return new Promise((resolve) => {
        if (this.debug) {
          console.log("history.go(" + delta + ")");
        }
        this.manualStateAction = true;
        history.go(delta);
        let timer = void 0;
        let called = false;
        const startTime = /* @__PURE__ */ new Date();
        const listener = () => {
          if (called) return;
          called = true;
          clearTimeout(timer);
          window.removeEventListener("popstate", listener);
          const end = /* @__PURE__ */ new Date();
          if (this.debug) {
            const ms = end.getTime() - startTime.getTime();
            console.log("Waiting for popstate event took " + ms + "ms");
          }
          setTimeout(() => {
            this.manualStateAction = false;
            resolve();
          }, 0);
        };
        window.addEventListener("popstate", listener);
        timer = setTimeout(() => {
          console.warn("Timeout while waiting for history.go");
          listener();
        }, 1e3);
      });
    });
  }
  getStateUrl(index) {
    if (index < 0) {
      return new ReactiveUrl({ url: "" });
    }
    if (!this.states[index]) {
      return this.getStateUrl(index - 1);
    }
    if (this.states[index].url !== null) {
      return this.states[index].url;
    }
    return this.getStateUrl(index - 1);
  }
  resolveUrl(index) {
    return this.getStateUrl(index).transformedHref;
  }
  /// Set the current URL without modifying states
  setUrl(url, title, index) {
    if (!this.active) {
      return;
    }
    if (this.debug) {
      console.log("Set url: " + (url == null ? void 0 : url.url) + ", for index " + index + " with current counter: " + this.counter, title);
    }
    if (index === void 0 || index === this.counter) {
      const state = this.states[this.states.length - 1];
      const count = state.index;
      if (this.changeUrlTimeout) {
        clearTimeout(this.changeUrlTimeout);
      }
      const didJustLoadPage = Date.now() - this.pageLoadedAt < 2e3;
      this.changeUrlTimeout = setTimeout(() => {
        this.changeUrlTimeout = null;
        if (this.counter !== count || state.url !== url) {
          return;
        }
        this.addToQueue(() => {
          if (this.counter !== count || state.url !== url) {
            return;
          }
          if (this.debug) {
            console.log("history.replaceState", count, url);
          }
          const formattedUrl = this.resolveUrl(count);
          history.replaceState({ counter: count }, "", formattedUrl);
          if (state.title) {
            window.document.title = this.formatTitle(state.title);
          }
        });
      }, didJustLoadPage ? 200 : 20);
      state.url = url;
      if (title) {
        state.title = title;
      }
    } else {
      const state = this.states[index];
      if (!state) {
        console.error("Search state with index ", index, "but no such state found");
        return;
      }
      if (state.index !== index) {
        console.error("Search state with index ", index, "but received state with index", state.index);
        return;
      }
      if (state.url !== url) {
        if (this.debug) {
          console.info("Changed url for old state: " + state.index + " to " + url);
        }
      }
      state.url = url;
      if (title) {
        state.title = title;
      }
    }
  }
  // Call this when url formatting or prefix has changed
  async updateUrl() {
    if (!this.active) {
      return;
    }
    if (this.changeUrlTimeout) {
      await this.waitForQueue();
      return;
    }
    const state = this.states[this.states.length - 1];
    const count = state.index;
    this.addToQueue(() => {
      if (this.counter !== count) {
        return;
      }
      if (this.debug) {
        console.log("history.replaceState - updateUrl");
      }
      const formattedUrl = this.resolveUrl(count);
      history.replaceState({ counter: count }, "", formattedUrl);
    });
    await this.waitForQueue();
  }
  formatTitle(title) {
    return title + (this.titleSuffix ? " | " + this.titleSuffix : "");
  }
  /**
   * Set the saved title for a given state. If that state is the current one, it will also get set immediately
   */
  setTitle(title, index) {
    if (!this.active) {
      return;
    }
    if (this.debug) {
      console.log("Set title: " + title + ", for index " + (index ?? this.counter) + " with current counter: " + this.counter);
    }
    if (index === void 0 || index === this.counter) {
      const state = this.states[this.states.length - 1];
      window.document.title = this.formatTitle(title);
      state.title = title;
    } else {
      const state = this.states[index];
      if (!state) {
        console.error("Search state with index ", index, "not found");
        return;
      }
      if (state.index !== index) {
        console.error("Search state with index ", index, "but received state with index", state.index);
        return;
      }
      state.title = title;
    }
  }
  getCurrentState() {
    return this.states[this.counter];
  }
  getState(index) {
    if (index < 0) {
      return null;
    }
    const s = this.states[index];
    if (s && s.index === index) {
      return s;
    }
    return this.getState(index - 1);
  }
  pushState(url, undoAction, options) {
    if (!this.active) {
      return;
    }
    this.counter++;
    if (this.counter !== this.states.length) {
      console.error("Invalid initial history item when pushing state");
    }
    const state = {
      url: url ?? null,
      index: this.counter,
      adjustHistory: true,
      undoAction,
      invalid: false,
      ...options
    };
    this.states.push(state);
    const c = this.counter;
    if (this.debug) {
      console.log("HistoryManager.pushState", state);
    }
    if (state.adjustHistory) {
      this.addToQueue(async () => {
        await awaitPrerenderingFinished();
        if (this.debug) {
          console.log("history.pushState", c, url);
        }
        const formattedUrl = url == null ? void 0 : url.transformedHref;
        history.pushState({ counter: c }, "", formattedUrl);
      });
    } else {
      this.addToQueue(() => {
        if (this.debug) {
          console.log("history.replaceState", c);
        }
        history.replaceState({ counter: c }, "", void 0);
      });
    }
  }
  /**
   * Call when an action is performed that breaks back/forward navigation
   */
  invalidateHistory() {
    if (this.debug) {
      console.log("HistoryManger.invalidateHistory");
    }
    for (const state of this.states) {
      state.undoAction = null;
      state.invalid = true;
    }
  }
  /**
   * Return to a given history point in time, if needed
   */
  returnToHistoryIndex(counter) {
    var _a;
    if (counter < 0) {
      counter = 0;
    }
    if (this.debug) {
      console.log("Did return to history index " + counter + ", coming from " + this.counter);
    }
    if (counter > this.counter) {
      console.warn("Performed non-compatible navigation. Probably because side-by-side views navigating");
      this.invalidateHistory();
      for (let i = this.counter + 1; i <= counter; i++) {
        if (i !== this.states.length) {
          console.error("Invalid history item at index", i, "when returning to history index");
        }
        this.states.push({
          index: i,
          adjustHistory: false,
          url: null,
          title: void 0,
          invalid: true,
          undoAction: null
        });
      }
      this.counter = counter;
    }
    if (counter < this.counter) {
      this.counter = counter;
      const deletedStates = this.states.splice(this.counter + 1);
      console.log("Deleted states", deletedStates.length);
      const adjustHistoryCount = deletedStates.filter((state) => state.adjustHistory).length;
      if (adjustHistoryCount > 0) {
        if (this.debug) {
          console.log("Adjusting browser history state: popping " + adjustHistoryCount + " items");
        }
        this.go(-adjustHistoryCount);
      }
    }
    if (!this.states[this.counter]) {
      console.error("Unexpected missing state", this.counter, this.states);
    } else if (this.states[this.counter].url) {
      if (this.debug) {
        console.log("Setting manual url without history api: " + ((_a = this.states[this.counter].url) == null ? void 0 : _a.toString()));
      }
      this.setUrl(this.states[this.counter].url, this.states[this.counter].title);
    }
    return this.counter;
  }
  activate() {
    history.scrollRestoration = "manual";
    async function onPopState(event) {
      var _a, _b;
      if (this.debug) {
        console.log("HistoryManager popstate", (_a = event.state) == null ? void 0 : _a.counter);
      }
      if (this.isAdjustingState) {
        console.warn("Duplicate popstate");
        return;
      }
      if (this.manualStateAction) {
        return;
      }
      const newCounter = (_b = event.state) == null ? void 0 : _b.counter;
      this.isAdjustingState = true;
      if (newCounter !== void 0) {
        if (newCounter > this.counter) {
          if (this.debug) {
            console.log("Reloading page because going forward is not supported");
          }
          window.location.reload();
          return;
        } else {
          const animate = this.counter - newCounter == 1 && this.animateHistoryPop;
          this.counter = newCounter;
          const deletedStates = this.states.splice(this.counter + 1).reverse();
          const newState = this.states[this.counter];
          if (newState.invalid) {
            console.warn("Reloading page bacause of invalid history", newState);
            window.location.reload();
            return;
          }
          for (const state of deletedStates) {
            if (state.invalid) {
              console.warn("Reloading page bacause of invalid history", state);
              window.location.reload();
              return;
            }
          }
          for (const state of deletedStates) {
            if (state.undoAction) {
              if (this.debug) {
                console.log("Executing undoAction...");
              }
              await state.undoAction(animate);
            } else {
              if (state.adjustHistory) {
                break;
              }
            }
          }
        }
      }
      this.isAdjustingState = false;
    }
    window.addEventListener("popstate", (event) => {
      onPopState.call(this, event).catch(console.error);
    });
    const clickHandler = () => {
      this.pageLoadedAt = 0;
    };
    document.addEventListener("pointerdown", clickHandler, { once: true, passive: true });
    this.active = true;
    if (history.state && history.state.counter !== void 0 && typeof history.state.counter === "number") {
      for (let i = 0; i < history.state.counter; i++) {
        if (i !== this.states.length) {
          console.error("Invalid history item at index", i, "when activating history manager");
        }
        this.states.push({
          index: i,
          adjustHistory: false,
          url: null,
          title: void 0,
          invalid: true,
          undoAction: null
        });
      }
      this.counter = history.state.counter;
    }
    history.replaceState({ counter: this.counter }, "");
    if (this.counter !== this.states.length) {
      console.error("Invalid initial history item when activating history manager");
    }
    this.states.push({
      index: this.counter,
      adjustHistory: false,
      url: null,
      title: void 0,
      invalid: false,
      undoAction: null
    });
  }
}
const HistoryManager = new HistoryManagerStatic();
function useCurrentComponent() {
  return inject("navigation_currentComponent", null);
}
function useParentComponent() {
  return inject("navigation_parentComponent", null);
}
function getExposeProxy(instance) {
  if (!instance) {
    return;
  }
  if (!instance.exposed) {
    return;
  }
  if (instance.exposeProxy) {
    return instance.exposeProxy;
  }
  const extendingProxy = instance.proxy;
  instance.exposeProxy = new Proxy(proxyRefs(markRaw(instance.exposed)), {
    get(target, key) {
      if (key in target) {
        return target[key];
      }
      return extendingProxy[key];
    },
    has(target, key) {
      return key in target || key in extendingProxy;
    }
  });
  return instance.exposeProxy;
}
class HistoryIndex {
  constructor(index) {
    __publicField(this, "index");
    this.index = index;
  }
}
function deepClone(root, alreadyProcessed) {
  const handled = alreadyProcessed ?? /* @__PURE__ */ new Map();
  const replaced = root.clone();
  replaced.properties = { ...replaced.properties };
  handled.set(root, replaced);
  for (const key in replaced.properties) {
    let v = replaced.properties[key];
    if (v instanceof ComponentWithProperties) {
      replaced.properties[key] = deepClone(v, handled);
    }
    if (Array.isArray(v) && v.length) {
      replaced.properties[key] = [...v];
      v = replaced.properties[key];
      for (let index = 0; index < v.length; index++) {
        const element = v[index];
        if (element instanceof ComponentWithProperties) {
          v[index] = deepClone(element, handled);
        }
      }
    }
  }
  return replaced;
}
function forAllRoots(root, handler, alreadyProcessed) {
  const handled = alreadyProcessed ?? /* @__PURE__ */ new Set();
  handler(root);
  handled.add(root);
  for (const key in root.properties) {
    const v = root.properties[key];
    if (v instanceof ComponentWithProperties) {
      forAllRoots(v, handler, handled);
    }
    if (Array.isArray(v) && v.length) {
      const lastItem = v[v.length - 1];
      if (lastItem instanceof ComponentWithProperties) {
        forAllRoots(lastItem, handler, handled);
      }
    }
  }
}
const _ComponentWithProperties = class _ComponentWithProperties {
  constructor(component, properties = {}, options) {
    /// Name of component or component Options. Currently no way to force type
    __publicField(this, "component");
    __publicField(this, "properties");
    __publicField(this, "provide");
    __publicField(this, "inheritedDisplayerProvide", {});
    __publicField(this, "inheritedParentProvide", {});
    __publicField(this, "key");
    __publicField(this, "type", null);
    /// Saved vnode of this instance
    __publicField(this, "vnode", null);
    __publicField(this, "unmount", null);
    // Keep the vnode alive when it is removed from the VDOM
    __publicField(this, "keepAlive", false);
    __publicField(this, "isKeptAlive", false);
    /// Cover whole screen. Other style = popup
    __publicField(this, "modalDisplayStyle", "cover");
    // If the display animation should be animated
    __publicField(this, "animated", true);
    __publicField(this, "checkRoutes", false);
    // Setting this will allow the component to check the routes on the next activation
    __publicField(this, "navigationRoutes", []);
    __publicField(this, "keepNavigationRoutes", false);
    __publicField(this, "isDismissing", ref(false));
    // Custom state for stack items that need to navigate way without being removed from the dom
    // Hisotry index
    __publicField(this, "historyIndex", null);
    __publicField(this, "forceCanHaveFocus", ref(false));
    this.component = component;
    this.key = _ComponentWithProperties.keyCounter++;
    this.properties = reactive(properties);
    this.provide = (options == null ? void 0 : options.provide) || {};
    this.inheritedDisplayerProvide = (options == null ? void 0 : options.inheritedDisplayerProvide) || {};
    this.inheritedParentProvide = (options == null ? void 0 : options.inheritedParentProvide) || {};
    this.forceCanHaveFocus = ref((options == null ? void 0 : options.forceCanHaveFocus) || false);
    this.keepNavigationRoutes = (options == null ? void 0 : options.keepNavigationRoutes) ?? false;
    markRaw(this);
  }
  // Automatically true if it has a history state
  // private static ignoreActivate: ComponentWithProperties | null = null
  setCheckRoutes() {
    forAllRoots(this, (c) => {
      c.checkRoutes = true;
    });
    return this;
  }
  get combinedProvide() {
    return {
      ...this.inheritedParentProvide,
      ...this.inheritedDisplayerProvide,
      // has priority
      ...this.provide
    };
  }
  inheritFromDisplayer(component) {
    this.inheritedDisplayerProvide = {
      ...component.combinedProvide
    };
  }
  inheritFromParent(component) {
    this.inheritedParentProvide = {
      ...component.combinedProvide
    };
  }
  clone() {
    return new _ComponentWithProperties(this.component, this.properties, { provide: this.provide, inheritedParentProvide: this.inheritedParentProvide, inheritedDisplayerProvide: this.inheritedDisplayerProvide });
  }
  deepClone() {
    return deepClone(this);
  }
  beforeMount() {
    if (this.vnode) {
      if (this.isKeptAlive) {
        this.isKeptAlive = false;
        _ComponentWithProperties.keepAliveCounter--;
        if (_ComponentWithProperties.debug) console.log("Total components kept alive: " + _ComponentWithProperties.keepAliveCounter);
      } else {
        if (_ComponentWithProperties.debug) console.warn("About to mount a component that was not destroyed properly " + this.component.name);
        this.destroy(this.vnode);
      }
    }
  }
  onUnmounted() {
  }
  getHistoryIndex() {
    if (this.component) return this.historyIndex;
  }
  deleteHistoryIndex() {
    this.historyIndex = null;
  }
  hasHistoryIndex() {
    return this.historyIndex !== null;
  }
  canHaveFocus() {
    return this.hasHistoryIndex() || unref(this.forceCanHaveFocus);
  }
  /**
   * This will get called when the component has been pushed somewhere that should count as a new history state
   */
  assignHistoryIndex() {
    if (!HistoryManager.active) {
      return;
    }
    const state = HistoryManager.getCurrentState();
    if (this.historyIndex !== null) {
      this.returnToHistoryIndex();
      return;
    }
    this.historyIndex = new HistoryIndex(state.index);
    if (_ComponentWithProperties.debug) console.warn("Assigned index to component", this.component.name ?? this, state.index);
    _ComponentWithProperties.historyIndexOwners.set(state.index, this);
  }
  inheritHistoryIndex(index) {
    if (this.historyIndex === null) {
      this.historyIndex = index;
    }
  }
  ownsHistoryIndex() {
    return this.historyIndex !== null && _ComponentWithProperties.historyIndexOwners.get(this.historyIndex.index) === this;
  }
  overrideUrl(url, title) {
    this.provide.reactive_navigation_url = url;
    this.setUrl(url, title);
  }
  setUrl(url, title) {
    if (this.historyIndex === null) {
      if (_ComponentWithProperties.debug) console.warn("Tried calling .setUrl on a component that was never assigned a history index. Check if you displayed this component using .show or .present");
      return;
    }
    if (!HistoryManager.active) {
      return;
    }
    if (!this.ownsHistoryIndex()) {
      return;
    }
    HistoryManager.setUrl(url, title, this.historyIndex.index);
  }
  setTitle(title) {
    if (!title) {
      return;
    }
    if (this.historyIndex === null) {
      if (_ComponentWithProperties.debug) console.warn("Tried calling .setTitle on a component that was never assigned a history index. Check if you displayed this component using .show or .present");
      return;
    }
    if (!HistoryManager.active) {
      return;
    }
    HistoryManager.setTitle(title, this.historyIndex.index);
  }
  /**
   * This will get called when the user returned to this component
   */
  returnToHistoryIndex() {
    if (!HistoryManager.active) {
      return false;
    }
    const instance = this.componentInstance();
    if (instance == null ? void 0 : instance.returnToHistoryIndex) {
      const worked = instance == null ? void 0 : instance.returnToHistoryIndex();
      if (_ComponentWithProperties.debug) console.log("returning to instance that has an instance with custom returnToHistoryIndex method", this.component.name, worked);
      if (worked === true) {
        return true;
      }
    }
    if (this.historyIndex === null) {
      if (_ComponentWithProperties.debug) console.warn("Returning to a component that has no history index assigned. Has this component been pushed to a navigation controller properly before returning to it?", this.component.name);
      return false;
    }
    if (_ComponentWithProperties.debug) console.warn("Returned to component", this.component.name ?? this);
    HistoryManager.returnToHistoryIndex(this.historyIndex.index);
    return true;
  }
  componentInstance() {
    var _a, _b, _c, _d;
    if (!((_a = this.vnode) == null ? void 0 : _a.component)) {
      return null;
    }
    return getExposeProxy((_b = this.vnode) == null ? void 0 : _b.component) || ((_d = (_c = this.vnode) == null ? void 0 : _c.component) == null ? void 0 : _d.proxy);
  }
  /**
   * Consults the mounted view's exposed `shouldNavigateAway(): boolean | Promise<boolean>`.
   * Missing guards allow leaving. A false result vetoes removal; rejected promises propagate.
   * Controllers call this for pop, dismiss and replacement unless `force` is true,
   * not when merely covering a view with another view.
   */
  async shouldNavigateAway() {
    const instance = this.componentInstance();
    if (instance && instance.shouldNavigateAway) {
      const promise = instance.shouldNavigateAway();
      if (typeof promise === "boolean") {
        if (!promise) {
          return false;
        }
      } else if (promise.then && promise.catch) {
        const r = await promise;
        if (!r) {
          return false;
        }
      }
    }
    return true;
  }
  destroy(vnode) {
    if (this.vnode) {
      if (vnode !== this.vnode) {
        if (_ComponentWithProperties.debug) console.warn("Received destroy event from old/different vnode", this.vnode, vnode);
        return;
      }
      if (this.keepAlive) {
        this.keepAlive = false;
        if (!this.isKeptAlive) {
          this.isKeptAlive = true;
          _ComponentWithProperties.keepAliveCounter++;
          if (_ComponentWithProperties.debug) console.log("Kept component alive " + this.component.name);
          if (_ComponentWithProperties.debug) console.log("Total components kept alive: " + _ComponentWithProperties.keepAliveCounter);
        }
        return;
      }
      if (this.isKeptAlive) {
        this.isKeptAlive = false;
        _ComponentWithProperties.keepAliveCounter--;
        if (_ComponentWithProperties.debug) console.log("Freed component from alive stack " + this.component.name);
        if (_ComponentWithProperties.debug) console.log("Total components kept alive: " + _ComponentWithProperties.keepAliveCounter);
      }
      if (_ComponentWithProperties.debug) console.log("Destroyed component " + this.component.name, this.vnode);
      if (this.unmount) {
        this.unmount(this.vnode);
        this.unmount = null;
      } else {
        console.error("No unmount function for component " + this.vnode);
      }
      this.deleteHistoryIndex();
      this.vnode = null;
      if (!this.keepNavigationRoutes) {
        this.navigationRoutes = [];
      }
    }
  }
  /**
   * @deprecated
   */
  setDisplayStyle(style) {
    this.modalDisplayStyle = style;
    return this;
  }
  /**
   * @deprecated
   */
  setAnimated(animated) {
    this.animated = animated;
    return this;
  }
};
// Counter for debugging. Count of components that are kept alive but are not mounted.
__publicField(_ComponentWithProperties, "keepAliveCounter", 0);
__publicField(_ComponentWithProperties, "keyCounter", 0);
__publicField(_ComponentWithProperties, "debug", false);
__publicField(_ComponentWithProperties, "historyIndexOwners", /* @__PURE__ */ new Map());
let ComponentWithProperties = _ComponentWithProperties;
const _hoisted_1$3 = { class: "stack-component" };
const _sfc_main$3 = /* @__PURE__ */ defineComponent({
  __name: "StackComponent",
  emits: ["returnToHistoryIndex"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const children = ref([]);
    const parentIsFocused = useFocused();
    const emit = __emit;
    provide("navigation_historyIndex", null);
    provide("navigation_parent", null);
    provide("reactive_navigation_url", null);
    provide("reactive_navigation_disable_url", null);
    function getCustomProvide(index, key) {
      return {
        reactive_navigation_pop: () => {
          removeAt(index, key);
        },
        reactive_navigation_can_pop: true,
        reactive_navigation_dismiss: () => {
          console.warn("Avoid calling dismiss in components on the StackComponent, since options are not supported here");
          removeAt(index, key);
        },
        reactive_navigation_can_dismiss: false,
        reactive_navigation_focused: parentIsFocused.value && getFocusedComponent() === children.value[index]
      };
    }
    function removeAt(index, key) {
      if (!children.value[index]) {
        for (const [i, comp] of children.value.entries()) {
          if (comp.key === key) {
            console.warn("Corrected index from " + index + " to " + i);
            index = i;
            break;
          }
        }
      }
      if (children.value[index] !== void 0 && children.value[index].key === key) {
        const hadFocus = children.value[index].hasHistoryIndex();
        children.value.splice(index, 1);
        if (hadFocus) {
          const newFocused = getComponentWithHistory();
          if (!newFocused) {
            emit("returnToHistoryIndex");
          } else {
            newFocused.returnToHistoryIndex();
          }
        }
      } else {
        console.warn("Expected component with key " + key + " at index " + index);
      }
    }
    function getComponentWithHistory() {
      for (let i = children.value.length - 1; i >= 0; i--) {
        if (children.value[i].hasHistoryIndex() && !children.value[i].isDismissing.value) {
          return children.value[i];
        }
      }
      return null;
    }
    function getFocusedComponent() {
      for (let i = children.value.length - 1; i >= 0; i--) {
        if (children.value[i].canHaveFocus() && !children.value[i].isDismissing.value) {
          return children.value[i];
        }
      }
      return null;
    }
    function show(component) {
      children.value.push(component);
    }
    onBeforeUnmount(() => {
      children.value = [];
    });
    __expose({
      show,
      getFocusedComponent,
      removeAt,
      children
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$3, [
        createVNode(TransitionGroup, {
          name: "show",
          duration: 300
        }, {
          default: withCtx(() => [
            (openBlock(true), createElementBlock(Fragment, null, renderList(children.value, (component, index) => {
              return openBlock(), createBlock(unref(ComponentWithPropertiesInstance), {
                key: component.key,
                component,
                "custom-provide": getCustomProvide(index, component.key)
              }, null, 8, ["component", "custom-provide"]);
            }), 128))
          ]),
          _: 1
        })
      ]);
    };
  }
});
const _hoisted_1$2 = { class: "modal-stack-component" };
const _sfc_main$2 = /* @__PURE__ */ defineComponent({
  __name: "ModalStackComponent",
  props: {
    root: {},
    initialComponents: { default: null },
    initialPresents: { default: null },
    disableAnimations: { type: Boolean, default: false }
  },
  setup(__props, { expose: __expose }) {
    const props = __props;
    const navigationController = ref(null);
    const stackComponent = ref(null);
    const parentIsFocused = useFocused();
    const instance = getCurrentInstance();
    provide("reactive_modalStackComponent", computed(() => getExposeProxy(instance)));
    provide("reactive_navigation_present", present);
    onMounted(async () => {
      if (props.initialPresents) {
        for (const p of props.initialPresents) {
          await present(p);
        }
      }
    });
    function returnToHistoryIndex() {
      var _a;
      return (_a = navigationController.value) == null ? void 0 : _a.returnToHistoryIndex();
    }
    const navigationFocused = computed(() => {
      var _a;
      return parentIsFocused.value && !((_a = stackComponent.value) == null ? void 0 : _a.getFocusedComponent());
    });
    const customProvide = computed(() => {
      return {
        reactive_navigation_focused: navigationFocused
      };
    });
    async function present(options) {
      var _a, _b, _c, _d, _e;
      const component = options.components[options.components.length - 1];
      if (options.animated !== void 0) {
        component.animated = options.animated;
      }
      const style = options.modalDisplayStyle ?? component.modalDisplayStyle ?? "cover";
      component.setDisplayStyle(style);
      if ((style === "popup" || style === "sheet" || style === "side-view") && (options.forceModalDisplay === true || ((_a = stackComponent.value) == null ? void 0 : _a.$el).offsetWidth > 800 || style === "sheet" && ((_b = stackComponent.value) == null ? void 0 : _b.$el).offsetWidth > 700)) {
        const c = new ComponentWithProperties(_sfc_main$1, {
          root: wrapWithNavigationController(component),
          // Fixes animation issues because navigation controller animates width and height changes
          className: options.modalClass ?? style,
          style: options.modalCssStyle ?? void 0
        });
        c.inheritFromDisplayer(component);
        const adjustHistory = (options == null ? void 0 : options.adjustHistory) ?? true;
        HistoryManager.pushState(null, adjustHistory ? async (canAnimate) => {
          var _a2;
          await ((_a2 = c.componentInstance()) == null ? void 0 : _a2.pop({ animated: canAnimate }));
        } : null, {
          adjustHistory,
          invalid: options.invalidHistory ?? false
        });
        c.assignHistoryIndex();
        (_c = stackComponent.value) == null ? void 0 : _c.show(c);
        return;
      }
      if (style === "overlay") {
        (_d = stackComponent.value) == null ? void 0 : _d.show(component);
        return;
      }
      await ((_e = navigationController.value) == null ? void 0 : _e.push(options));
    }
    function replace(component, animated = true) {
      var _a;
      const nav = navigationController.value;
      nav == null ? void 0 : nav.push({
        components: [component],
        animated,
        replace: ((_a = nav == null ? void 0 : nav.components) == null ? void 0 : _a.length) ?? 0
      });
    }
    __expose({
      present: shallowRef(present),
      stackComponent,
      replace
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1$2, [
        createVNode(NavigationController, {
          ref_key: "navigationController",
          ref: navigationController,
          "animation-type": "modal",
          "disable-animations": __props.disableAnimations,
          root: __props.root,
          "initial-components": __props.initialComponents,
          "custom-provide": customProvide.value
        }, null, 8, ["disable-animations", "root", "initial-components", "custom-provide"]),
        createVNode(_sfc_main$3, {
          ref_key: "stackComponent",
          ref: stackComponent,
          onReturnToHistoryIndex: returnToHistoryIndex
        }, null, 512)
      ]);
    };
  }
});
const ModalStackComponentFinderMixin = defineComponent({
  computed: {
    modalStackComponent() {
      let start = this.$parent;
      while (start) {
        if (start instanceof _sfc_main$2) {
          return start;
        }
        start = start.$parent;
      }
      return null;
    }
  }
});
function useNavigationController() {
  const c = inject("reactive_navigationController");
  return shallowRef(c);
}
function useSplitViewController() {
  const c = inject("reactive_splitViewController");
  return shallowRef(c);
}
function injectHooks(instanceProxy, definitions) {
  const ctx = instanceProxy.$.ctx;
  for (const key in definitions) {
    if (!isRef(definitions[key])) {
      ctx[key] = definitions[key];
    } else {
      const val = definitions[key];
      Object.defineProperty(ctx, key, {
        enumerable: true,
        configurable: true,
        get: () => {
          return val.value;
        },
        set: () => {
          warn(`Cannot assign to '${key}' of navigation mixin. This is a read-only property.`);
        }
      });
    }
  }
}
function useModalStackComponent() {
  const c = inject("reactive_modalStackComponent");
  return c;
}
function usePopup() {
  return inject("reactive_popup", null);
}
const navigationMethods = {
  setTitle() {
    var _a;
    const navigationOptions = (_a = this.$options) == null ? void 0 : _a.navigation;
    if (!navigationOptions) return;
    const title = navigationOptions.title;
    if (typeof title === "function") {
      this.$url.setTitle(title.call(this));
    } else {
      this.$url.setTitle(title ?? "");
    }
  }
};
const NavigationMixin = {
  created() {
    const definitions = {
      pop: usePop(),
      showDetail: useShowDetail(),
      show: useShow(),
      present: usePresent(),
      dismiss: useDismiss(),
      canPop: useCanPop(),
      canDismiss: useCanDismiss(),
      isFocused: useFocused(),
      emitParents: () => {
        throw new Error("emitParents has been removed and should no longer be needed");
      },
      popup: usePopup(),
      modalStackComponent: useModalStackComponent(),
      navigationController: useNavigationController(),
      splitViewController: useSplitViewController(),
      $url: useUrl(),
      $navigate: useNavigate()
    };
    injectHooks(this, definitions);
  },
  computed: {
    modalNavigationController() {
      return this.modalStackComponent.navigationController;
    }
  },
  activated() {
    navigationMethods.setTitle.call(this);
  }
};
const _hoisted_1$1 = ["inert"];
const _hoisted_2$1 = { class: "scrollable-container" };
const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  __name: "Popup",
  props: {
    root: {},
    className: { default: "popup" },
    style: { default: "" }
  },
  setup(__props, { expose: __expose }) {
    const instance = getCurrentInstance();
    const Popup = instance.type;
    const props = __props;
    const hide = ref(false);
    const modalStackComponent = useModalStackComponent();
    const pop = usePop();
    const mainContent = ref(null);
    const component = useCurrentComponent();
    provide("reactive_navigation_dismiss", async (options) => {
      return await dismiss(options);
    });
    provide("reactive_navigation_pop", async (options) => {
      console.warn("Using .pop() inside a Popup without a NavigationController dismisses the Popup. It is recommended to use .dismiss() instead.");
      return await dismiss(options);
    });
    provide("reactive_navigation_can_pop", false);
    provide("reactive_navigation_can_dismiss", true);
    provide("reactive_popup", computed(() => getExposeProxy(instance)));
    const pushDown = computed(() => {
      var _a, _b;
      const popups = ((_b = (_a = modalStackComponent.value) == null ? void 0 : _a.stackComponent) == null ? void 0 : _b.children.filter((c) => c.component === Popup && (c.properties.className ?? "popup") === (props.className ?? "popup") && !c.isDismissing.value)) ?? [];
      if (popups.length > 0 && popups[popups.length - 1] !== component) {
        if (popups.length > 1 && popups[popups.length - 2] === component) {
          return 1;
        }
        return 2;
      }
      return 0;
    });
    const buildClass = computed(() => {
      const vvv = { "push-down": pushDown.value == 1, "push-down-full": pushDown.value > 1 };
      const j = Object.keys(vvv).filter((p) => !!vvv[p]).join(" ");
      return j + (j ? " " : "") + (props.className ? props.className : "popup") + (props.root.animated ? " animated" : "") + (isFocused.value ? " focused" : "");
    });
    const isFocused = useFocused();
    const onKey = (event) => {
      if (event.defaultPrevented || event.repeat) {
        return;
      }
      if (!isFocused.value) {
        return;
      }
      const key = event.key || event.keyCode;
      if (key === "Escape" || key === "Esc" || key === 27) {
        dismiss().catch(console.error);
        event.preventDefault();
      }
    };
    const shouldNavigateAway = () => {
      return props.root.shouldNavigateAway();
    };
    const dismiss = async (options) => {
      var _a;
      if (hide.value) {
        return;
      }
      if (!(options == null ? void 0 : options.force)) {
        const r = await shouldNavigateAway();
        if (!r) {
          return false;
        }
      }
      component.isDismissing.value = true;
      (_a = pop(options)) == null ? void 0 : _a.catch(console.error);
    };
    const onClick = (event) => {
      if (mainContent.value && event.target && !mainContent.value.contains(event.target) && (event.target.isConnected || document.body.contains(event.target))) {
        dismiss().catch(console.error);
        event.preventDefault();
      }
    };
    onActivated(() => {
      document.addEventListener("keydown", onKey);
    });
    onDeactivated(() => {
      document.removeEventListener("keydown", onKey);
    });
    __expose({
      dismiss: shallowRef(dismiss),
      pop: shallowRef(dismiss)
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(buildClass.value),
        style: normalizeStyle(__props.style),
        inert: pushDown.value > 0,
        onClick
      }, [
        createElementVNode("div", {
          ref_key: "mainContent",
          ref: mainContent
        }, [
          createElementVNode("div", _hoisted_2$1, [
            (openBlock(), createBlock(unref(ComponentWithPropertiesInstance), {
              key: __props.root.key,
              component: __props.root
            }, null, 8, ["component"]))
          ])
        ], 512),
        _cache[0] || (_cache[0] = createElementVNode("div", { class: "background" }, null, -1))
      ], 14, _hoisted_1$1);
    };
  }
});
const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return function() {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};
const _sfc_main = defineComponent({
  name: "SplitViewController",
  components: {
    NavigationController,
    FramedComponent
  },
  provide() {
    return {
      reactive_splitViewController: this,
      reactive_navigation_show_detail: this.showDetail
    };
  },
  props: {
    /** Master/menu view, rendered in its own navigation stack. Detail navigation targets the second pane. */
    root: {
      required: true,
      type: Object
    },
    /** Optional CSS width for the detail pane; null uses the stylesheet default. */
    detailWidth: {
      default: null,
      type: String
    }
  },
  data() {
    return {
      detail: null,
      detailKey: null,
      defaultHandler: null,
      isChangingComponents: false
    };
  },
  computed: {
    masterProvide() {
      return {
        // The master cannot make changes to the url or title if there is a detail
        reactive_navigation_disable_url: computed(() => !!this.detail),
        reactive_provide_default_handler: (defaultHandler) => {
          this.defaultHandler = defaultHandler;
          this.onResize();
        }
      };
    },
    detailProvide() {
      return {
        // Detail can not alter the navigation of the parent of split view controller.
        reactive_navigation_can_pop: false,
        reactive_navigation_can_dismiss: false
      };
    },
    lastIsDetail() {
      var _a, _b;
      return this.detailKey != null && ((_b = (_a = this.navigationController) == null ? void 0 : _a.mainComponent) == null ? void 0 : _b.key) == this.detailKey;
    },
    navigationController() {
      return this.$refs["navigationController"];
    },
    masterElement() {
      return this.$refs["masterElement"];
    }
  },
  created() {
    const definitions = {
      $url: useUrl()
    };
    injectHooks(this, definitions);
  },
  mounted() {
    if (this.detailWidth) {
      this.$el.style.setProperty("--split-view-width", this.detailWidth);
    }
  },
  activated() {
    this.listener = throttle(this.onResize, 100);
    window.addEventListener("resize", this.listener, { passive: true });
    this.onResize();
  },
  deactivated() {
    window.removeEventListener("resize", this.listener, { passive: true });
  },
  methods: {
    returnToHistoryIndex() {
      if (this.detail) {
        return this.detail.returnToHistoryIndex();
      }
      return this.navigationController.returnToHistoryIndex();
    },
    onResize() {
      if (this.$el.isConnected === false) return false;
      if (this.shouldCollapse()) {
        if (this.detail) {
          this.collapse().catch(console.error);
        }
      } else {
        if (this.canExpand()) {
          this.expand().catch(console.error);
        }
      }
    },
    getScrollElement(element = null) {
      if (!element) {
        element = this.$el;
      }
      const style = window.getComputedStyle(element);
      if (style.overflowY == "scroll" || style.overflow == "scroll" || style.overflow == "auto" || style.overflowY == "auto") {
        return element;
      } else {
        if (!element.parentElement) {
          return document.documentElement;
        }
        return this.getScrollElement(element.parentElement);
      }
    },
    async shouldNavigateAway() {
      if (this.detail) {
        const r = await this.detail.shouldNavigateAway();
        if (!r) {
          return false;
        }
      }
      if (this.navigationController) {
        return await this.navigationController.shouldNavigateAway();
      }
      return true;
    },
    async showDetail(options) {
      if (this.isChangingComponents) {
        console.error("Show detail called on a splitViewController that is busy");
        return false;
      }
      const component = options.components[options.components.length - 1];
      this.detailKey = component.key;
      this.isChangingComponents = true;
      try {
        if (this.shouldCollapse()) {
          if (this.lastIsDetail || this.detail) {
            console.error("Pushing a detail when a detail is already presented is not allowed");
            this.isChangingComponents = false;
            return false;
          }
          await this.navigationController.push(options);
        } else {
          if (this.detail) {
            const r = await this.detail.shouldNavigateAway();
            if (!r) {
              this.isChangingComponents = false;
              return false;
            }
          }
          this.getScrollElement().scrollTop = 0;
          if (this.detail) {
            HistoryManager.invalidateHistory();
          }
          HistoryManager.pushState(null, null, {
            adjustHistory: options.adjustHistory ?? true,
            invalid: !!options.invalidHistory
          });
          component.assignHistoryIndex();
          this.detail = component;
        }
      } finally {
        this.isChangingComponents = false;
      }
      return true;
    },
    shouldCollapse() {
      return this.$el.offsetWidth < 850;
    },
    async collapse() {
      if (!this.navigationController) {
        console.error("Cannot collapse without navigation controller");
        return;
      }
      if (this.lastIsDetail) {
        console.error("Cannot collapse when the detail is already collaped");
        return;
      }
      if (!this.detail) {
        console.error("Cannot collapse without detail");
        return;
      }
      if (this.isChangingComponents) {
        console.error("Cannot collapse while already isChangingComponents");
        return;
      }
      console.log("Collapse SplitViewController");
      this.isChangingComponents = true;
      try {
        this.detail.keepAlive = true;
        const detail = this.detail;
        this.detail = null;
        await this.navigationController.push({ components: [detail], animated: false });
      } finally {
        this.isChangingComponents = false;
      }
    },
    canExpand() {
      if (!this.navigationController) {
        return false;
      }
      if (this.detail) {
        return false;
      }
      if (this.isChangingComponents) {
        return false;
      }
      if (!this.lastIsDetail) {
        if (this.defaultHandler) {
          return true;
        }
        return false;
      }
      return true;
    },
    async expand() {
      if (!this.navigationController) {
        console.error("Cannot expand without navigation controller");
        return;
      }
      if (this.detail) {
        console.error("Cannot expand when detail is already visible");
        return;
      }
      if (this.isChangingComponents) {
        console.error("Cannot expand while already isChangingComponents");
        return false;
      }
      console.log("Expand SplitViewController");
      if (!this.lastIsDetail) {
        if (!this.defaultHandler) {
          console.error("Cannot expand when there is no defaultHandler");
          return;
        }
        this.isChangingComponents = false;
        try {
          const succeeded = await this.defaultHandler();
          if (succeeded && !this.detail) {
            console.warn("Did call defaultHandler but no detail was set. Are all mounts properly awaited?");
          }
          if (!succeeded) {
            console.warn("Failed to show default handler");
          }
          console.info("Used default handler for split view controller");
        } finally {
          this.isChangingComponents = false;
        }
        return;
      }
      this.isChangingComponents = true;
      try {
        const popped = await this.navigationController.pop({
          animated: false,
          destroy: false
        });
        if (!popped || popped.length == 0) {
          this.isChangingComponents = false;
          return;
        }
        await this.$nextTick();
        HistoryManager.pushState(null, null, {
          adjustHistory: false,
          invalid: true
        });
        this.detailKey = popped[0].key;
        this.detail = popped[0];
        this.detail.assignHistoryIndex();
      } finally {
        this.isChangingComponents = false;
      }
    }
  }
});
const _hoisted_1 = ["data-has-detail"];
const _hoisted_2 = {
  ref: "masterElement",
  class: "master"
};
const _hoisted_3 = {
  key: 0,
  class: "detail"
};
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_NavigationController = resolveComponent("NavigationController");
  const _component_FramedComponent = resolveComponent("FramedComponent");
  return openBlock(), createElementBlock("div", {
    class: "split-view-controller",
    "data-has-detail": _ctx.detail ? "true" : "false"
  }, [
    createElementVNode("div", _hoisted_2, [
      createVNode(_component_NavigationController, {
        ref: "navigationController",
        root: _ctx.root,
        "custom-provide": _ctx.masterProvide
      }, null, 8, ["root", "custom-provide"])
    ], 512),
    _ctx.detail ? (openBlock(), createElementBlock("div", _hoisted_3, [
      (openBlock(), createBlock(_component_FramedComponent, {
        key: _ctx.detail.key,
        root: _ctx.detail,
        "custom-provide": _ctx.detailProvide
      }, null, 8, ["root", "custom-provide"]))
    ])) : createCommentVNode("", true)
  ], 8, _hoisted_1);
}
const SplitViewController = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render]]);
export {
  ComponentWithProperties,
  ComponentWithPropertiesInstance,
  FramedComponent,
  HistoryIndex,
  HistoryManager,
  _sfc_main$2 as ModalStackComponent,
  ModalStackComponentFinderMixin,
  NavigationController,
  NavigationMixin,
  _sfc_main$1 as Popup,
  ReactiveUrl,
  SplitViewController,
  _sfc_main$3 as StackComponent,
  UrlHelper,
  defineRoute,
  defineRoutes,
  extendUrl,
  forAllRoots,
  getExposeProxy,
  injectHooks,
  matchPath,
  matchQuery,
  mergeSearchParams,
  normalizePushOptions,
  onCheckRoutes,
  onNotCheckRoutes,
  setTitle,
  setTitleSuffix,
  setUrl,
  templateToUrl,
  typeRoute,
  useAnimateHeightChange,
  useCanDismiss,
  useCanPop,
  useCheckRoute,
  useContentState,
  useCurrentComponent,
  useCurrentHref,
  useDismiss,
  useFocused,
  useInheritCheckRoutes,
  useManualPresent,
  useModalStackComponent,
  useNavigate,
  useNavigationController,
  useParentComponent,
  usePop,
  usePopup,
  usePresent,
  useShow,
  useShowDetail,
  useSplitViewController,
  useUrl
};
